import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { ScrollArea } from './ui/scroll-area';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import {
  BarChart3, Download, Grid3X3, Shield, TrendingUp, Network, Building2, Layers, FileDown, Mail, GitCompareArrows,
  DollarSign, FileText, Star,
  Calendar, Target, AlertTriangle, Radar, Headphones,
  ChevronLeft, ChevronRight, ChevronDown, Clock,
  ArrowRight, Filter, X,
} from 'lucide-react';
import { jsPDF } from 'jspdf';
import { BulkSupportContactDialog, BulkGovernanceDialog, BulkFillGapsDialog } from './BulkEditors';
import { usePermissions } from './usePermissions';
import SidebarLayout from './SidebarLayout';
import { useColumnPicker } from './common/ColumnPicker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Map report UI IDs to RBAC permission IDs
const REPORT_PERM_MAP = {
  executive: 'report.executive_summary', maturity: 'report.maturity_scorecard', compare: 'report.property_compare',
  coverage: 'report.coverage_matrix', gaps: 'report.layer_gaps', onboarding: 'report.readiness_score',
  platformgap: 'report.platform_gap_matrix', platformutil: 'report.platform_utilization',
  integtypeadv: 'report.integration_type_advisory',
  integration: 'report.integration_gaps', integflow: 'report.integration_flow',
  brand: 'report.brand_compliance', brandgapdetail: 'report.brand_gap_detail',
  governance: 'report.governance', ssodetail: 'report.sso_coverage', credentials: 'report.credential_audit',
  adoption: 'report.solution_adoption', solutiondrift: 'report.solution_drift',
  vendors: 'report.vendor_concentration', vendordiversity: 'report.vendor_diversity',
  vendorrisk: 'report.vendor_risk', multivendor: 'report.multi_vendor',
  scorecards: 'report.vendor_scorecards', standardization: 'report.standardization',
  costs: 'report.cost_summary', costheatmap: 'report.cost_heatmap',
  contracts: 'report.contracts', renewals: 'report.renewal_calendar', corpvslocal: 'report.corporate_vs_local',
  techdebt: 'report.tech_debt', techage: 'report.tech_age', esg: 'report.esg_summary',
  supportinfo: 'report.support_completeness',
  vendormap: 'report.vendor_deployment_map',
  vendoradoption: 'report.vendor_product_adoption',
  vendorcontracts: 'report.vendor_contract_summary',
  vendoroverlap: 'report.vendor_overlap',
  brandscorecard: 'report.brand_compliance_scorecard',
  brandmatrix: 'report.brand_property_matrix',
  brandgaps: 'report.brand_standard_gaps',
  propinventory: 'report.property_solution_inventory',
};

const REPORT_GROUPS = [
  { label: 'Executive', reports: [
    { id: 'executive', label: 'Executive Summary', icon: BarChart3 },
    { id: 'maturity', label: 'Maturity Scorecard', icon: Radar },
    { id: 'compare', label: 'Property Compare', icon: GitCompareArrows },
  ]},
  { label: 'Coverage & Gaps', reports: [
    { id: 'coverage', label: 'Coverage Matrix', icon: Grid3X3 },
    { id: 'gaps', label: 'Layer Gap Analysis', icon: Layers },
    { id: 'onboarding', label: 'Onboarding Readiness', icon: Target },
    { id: 'platformgap', label: 'Platform Gap Matrix', icon: Grid3X3 },
    { id: 'platformutil', label: 'Platform Utilization', icon: BarChart3 },
  ]},
  { label: 'Integrations', reports: [
    { id: 'integration', label: 'Integration Gaps', icon: Network },
    { id: 'integflow', label: 'Integration Data Flow', icon: Network },
    { id: 'integtypeadv', label: 'Integration Type Advisory', icon: Network },
  ]},
  { label: 'Brand & Compliance', reports: [
    { id: 'brand', label: 'Brand Compliance', icon: Shield },
    { id: 'brandgapdetail', label: 'Brand Gap Detail', icon: Shield },
    { id: 'governance', label: 'Governance Matrix', icon: Shield },
    { id: 'ssodetail', label: 'SSO Coverage Detail', icon: Network },
    { id: 'credentials', label: 'Credential Audit', icon: Network },
  ]},
  { label: 'Vendors & Solutions', reports: [
    { id: 'adoption', label: 'Solution Adoption', icon: TrendingUp },
    { id: 'solutiondrift', label: 'Solution Drift', icon: AlertTriangle },
    { id: 'vendors', label: 'Vendor Concentration', icon: Building2 },
    { id: 'vendordiversity', label: 'Vendor Diversity', icon: Building2 },
    { id: 'vendorrisk', label: 'Vendor Risk Map', icon: AlertTriangle },
    { id: 'multivendor', label: 'Multi-Vendor Conflicts', icon: AlertTriangle },
    { id: 'scorecards', label: 'Vendor Scorecards', icon: Star },
    { id: 'standardization', label: 'Standardization Opps', icon: Target },
  ]},
  { label: 'Financial', reports: [
    { id: 'costs', label: 'Cost Summary', icon: DollarSign },
    { id: 'costheatmap', label: 'Cost Heatmap', icon: DollarSign },
    { id: 'contracts', label: 'Contracts', icon: FileText },
    { id: 'renewals', label: 'Renewal Calendar', icon: Calendar },
    { id: 'corpvslocal', label: 'Corporate vs Local', icon: Layers },
  ]},
  { label: 'Risk & Health', reports: [
    { id: 'techdebt', label: 'Tech Debt Index', icon: AlertTriangle },
    { id: 'techage', label: 'Tech Stack Age', icon: Calendar },
    { id: 'esg', label: 'ESG & Diversity', icon: Shield },
    { id: 'supportinfo', label: 'Support Completeness', icon: Headphones },
  ]},
  { label: 'Vendor Intelligence', reports: [
    { id: 'vendormap', label: 'Vendor Deployment Map', icon: Building2 },
    { id: 'vendoradoption', label: 'Vendor Product Adoption', icon: TrendingUp },
    { id: 'vendorcontracts', label: 'Vendor Contract Summary', icon: DollarSign },
    { id: 'vendoroverlap', label: 'Vendor Overlap Analysis', icon: AlertTriangle },
  ]},
  { label: 'Brand Intelligence', reports: [
    { id: 'brandscorecard', label: 'Brand Compliance Scorecard', icon: Shield },
    { id: 'brandmatrix', label: 'Brand Property Matrix', icon: Grid3X3 },
    { id: 'brandgaps', label: 'Brand Standard Gaps', icon: AlertTriangle },
  ]},
  { label: 'Property Intel', reports: [
    { id: 'propinventory', label: 'Property Solution Inventory', icon: Layers },
  ]},
];

const REPORT_TABS = REPORT_GROUPS.flatMap(g => g.reports);

export default function Reports({ token, role, hasPermission: externalHasPermission }) {
  const { hasPermission: hookHasPermission } = usePermissions(token);
  const hasPermission = externalHasPermission || hookHasPermission;
  const [activeReport, setActiveReport] = useState('executive');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(typeof window !== 'undefined' && window.innerWidth < 768);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');
  const [emailing, setEmailing] = useState(false);

  // Schedule dialog state
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({ recipients: '', frequency: 'weekly', day_of_week: 1, enabled: true });
  const [scheduling, setScheduling] = useState(false);
  const [schedules, setSchedules] = useState([]);
  const [showScheduleList, setShowScheduleList] = useState(false);
  const [favorites, setFavorites] = useState([]);

  // Property compare state
  const [compareProperties, setCompareProperties] = useState([]);
  const [allProperties, setAllProperties] = useState([]);

  // Property group filtering
  const [propertyGroups, setPropertyGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState('');

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // Fetch report favorites
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/user/report-favorites`, { headers })
      .then(res => setFavorites(res.data || []))
      .catch(() => {});
  }, [headers]);

  const toggleFavorite = async (reportId) => {
    const isPinned = !favorites.includes(reportId);
    const newFavs = isPinned ? [...favorites, reportId] : favorites.filter(f => f !== reportId);
    setFavorites(newFavs);
    try {
      await axios.put(`${BACKEND_URL}/api/user/report-favorites`, { report_id: reportId, pinned: isPinned }, { headers });
    } catch (e) { /* best effort */ }
  };

  // Check if user can view a specific report
  const canViewReport = useCallback((reportId) => {
    const permId = REPORT_PERM_MAP[reportId];
    if (!permId) return true; // No mapping = always visible
    return hasPermission(permId);
  }, [hasPermission]);

  // Filter groups to only show reports the user can access
  const filteredGroups = useMemo(() => REPORT_GROUPS.map(g => ({
    ...g,
    reports: g.reports.filter(r => canViewReport(r.id)),
  })).filter(g => g.reports.length > 0), [canViewReport]);

  // Schedule handlers
  const fetchSchedules = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/report-schedules`, { headers });
      setSchedules(res.data.schedules || []);
    } catch {}
  }, [headers]);

  const createSchedule = async () => {
    const reportLabel = REPORT_TABS.find(t => t.id === activeReport)?.label || activeReport;
    setScheduling(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/report-schedules`, {
        name: `${reportLabel} — ${scheduleForm.frequency}`,
        report_type: activeReport,
        recipients: scheduleForm.recipients.split(',').map(e => e.trim()).filter(Boolean),
        frequency: scheduleForm.frequency,
        day_of_week: parseInt(scheduleForm.day_of_week),
        enabled: scheduleForm.enabled,
      }, { headers });
      toast.success('Schedule created', { description: `${reportLabel} will be sent ${scheduleForm.frequency}` });
      setShowScheduleDialog(false);
      setScheduleForm({ recipients: '', frequency: 'weekly', day_of_week: 1, enabled: true });
      fetchSchedules();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create schedule'); }
    finally { setScheduling(false); }
  };

  const deleteSchedule = async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/report-schedules/${id}`, { headers });
      toast.success('Schedule deleted');
      fetchSchedules();
    } catch { toast.error('Failed'); }
  };

  const toggleSchedule = async (id, enabled) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/report-schedules/${id}`, { enabled }, { headers });
      fetchSchedules();
    } catch {}
  };

  useEffect(() => { fetchSchedules(); }, [fetchSchedules]);

  // Fetch property groups for filtering
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/property-groups`, { headers })
      .then(r => setPropertyGroups(r.data.groups || []))
      .catch(() => {});
  }, [headers]);

  // Re-fetch report when group filter changes
  useEffect(() => {
    if (activeReport) fetchReport(activeReport);
  }, [selectedGroupId]); // eslint-disable-line

  const fetchReport = useCallback(async (report) => {
    setLoading(true);
    setData(null); // Clear stale data to prevent cross-report type confusion
    const endpoints = {
      executive: '/api/reports/executive-summary',
      coverage: '/api/reports/coverage-matrix',
      brand: '/api/reports/brand-compliance',
      adoption: '/api/reports/solution-adoption',
      integration: '/api/reports/integration-gaps',
      maturity: '/api/reports/maturity-scorecard',
      vendors: '/api/reports/vendor-concentration',
      vendorrisk: '/api/reports/vendor-risk',
      standardization: '/api/reports/standardization',
      gaps: '/api/reports/layer-gaps',
      costs: '/api/reports/cost-summary',
      contracts: '/api/contracts',
      renewals: '/api/reports/renewal-calendar',
      scorecards: '/api/reports/vendor-scorecards',
      governance: '/api/reports/governance',
      credentials: '/api/reports/credential-audit',
      platformutil: '/api/reports/platform-utilization',
      solutiondrift: '/api/reports/solution-drift',
      integflow: '/api/reports/integration-flow',
      onboarding: '/api/reports/readiness-score?property_id=overall',
      vendordiversity: '/api/reports/vendor-diversity',
      techdebt: '/api/reports/tech-debt',
      corpvslocal: '/api/reports/corporate-vs-local',
      costheatmap: '/api/reports/cost-heatmap',
      esg: '/api/reports/esg-summary',
      techage: '/api/reports/property-tech-age',
      platformgap: '/api/reports/platform-gap-matrix',
      multivendor: '/api/reports/multi-vendor-properties',
      ssodetail: '/api/reports/sso-coverage-detail',
      brandgapdetail: '/api/reports/brand-gap-detail',
      supportinfo: '/api/reports/support-completeness',
      vendormap: '/api/reports/vendor-deployment-map',
      vendoradoption: '/api/reports/vendor-product-adoption',
      vendorcontracts: '/api/reports/vendor-contract-summary',
      vendoroverlap: '/api/reports/vendor-overlap',
      brandscorecard: '/api/reports/brand-compliance-scorecard',
      brandmatrix: '/api/reports/brand-property-matrix',
      brandgaps: '/api/reports/brand-standard-gaps',
      integtypeadv: '/api/reports/integration-type-advisory',
    };
    if (report === 'propinventory') {
      setLoading(false);
      return; // Self-fetching component
    }
    if (report === 'compare') {
      setCompareProperties([]); // Clear stale selections on tenant switch
      try {
        const res = await axios.get(`${BACKEND_URL}/api/properties`, { headers });
        setAllProperties(res.data || []);
      } catch { setAllProperties([]); }
      setLoading(false);
      return;
    }
    try {
      const url = `${BACKEND_URL}${endpoints[report]}`;
      const params = {};
      if (selectedGroupId) params.group_id = selectedGroupId;
      const res = await axios.get(url, { headers, params });
      setData(res.data);
    } catch {
      toast.error('Failed to load report');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [headers, selectedGroupId]);

  const fetchCompare = useCallback(async () => {
    if (compareProperties.length < 2) return;
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/reports/property-compare`, {
        headers, params: { property_ids: compareProperties.join(',') },
      });
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load comparison');
      setData(null);
      if (err.response?.status === 400 || err.response?.status === 404) {
        setCompareProperties([]);
      }
    } finally {
      setLoading(false);
    }
  }, [headers, compareProperties]);

  useEffect(() => { fetchReport(activeReport); }, [activeReport, fetchReport]);

  const exportPdf = () => {
    const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const tab = REPORT_TABS.find(t => t.id === activeReport);
    pdf.setFontSize(16);
    pdf.text(`LodgiFlow — ${tab?.label || 'Report'}`, 14, 15);
    pdf.setFontSize(8);
    pdf.setTextColor(100);
    pdf.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 21);

    // Render table from the visible report content
    const el = document.getElementById('report-content');
    if (el) {
      const rows = el.querySelectorAll('tr');
      let y = 28;
      rows.forEach((row, ri) => {
        const cells = row.querySelectorAll('td, th');
        let x = 14;
        pdf.setFontSize(ri === 0 ? 7 : 6.5);
        pdf.setTextColor(ri === 0 ? 30 : 80);
        cells.forEach(cell => {
          const text = cell.textContent?.substring(0, 25) || '';
          pdf.text(text, x, y);
          x += 32;
          if (x > 280) return;
        });
        y += 5;
        if (y > 195) { pdf.addPage(); y = 15; }
      });
    }

    pdf.save(`lodgiflow-${activeReport}-report.pdf`);
    toast.success('PDF downloaded');
  };

  const cellColor = (used, total) => {
    if (total === 0) return 'bg-slate-50 text-slate-400';
    const pct = used / total;
    if (pct >= 0.5) return 'bg-emerald-100 text-emerald-800';
    if (pct > 0) return 'bg-amber-100 text-amber-800';
    return 'bg-red-50 text-red-400';
  };

  return (
    <SidebarLayout
      title="Reports"
      icon={BarChart3}
      groups={[
        ...(favorites.length > 0 ? [{
          label: 'Favorites',
          items: favorites.map(fId => {
            const r = REPORT_TABS.find(t => t.id === fId);
            return r ? { id: r.id, label: r.label, icon: r.icon, badge: '★' } : null;
          }).filter(Boolean),
        }] : []),
        ...filteredGroups.map(g => ({
          label: g.label,
          items: g.reports.map(r => ({ id: r.id, label: r.label, icon: r.icon })),
        })),
      ]}
      activeId={activeReport}
      onSelect={(id) => setActiveReport(id)}
      footer={
        <button onClick={() => toggleFavorite(activeReport)} className="w-full text-xs text-center py-1.5 rounded-md hover:bg-muted/60 text-muted-foreground transition-colors" data-testid="toggle-report-favorite">
          {favorites.includes(activeReport) ? '★ Remove from Favorites' : '☆ Add to Favorites'}
        </button>
      }
    >
      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Export bar */}
        <div className="px-4 py-2 border-b bg-card/50 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-1.5">
            {schedules.filter(s => s.report_type === activeReport).length > 0 && (
              <Badge variant="outline" className="text-[9px] gap-1 text-emerald-600"><Clock className="h-2.5 w-2.5" /> Scheduled</Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
          {hasPermission('action.export_email') && (
            <Button size="sm" variant="outline" className="gap-1.5 text-xs" onClick={() => setShowScheduleDialog(true)} data-testid="schedule-report-btn">
              <Clock className="h-3.5 w-3.5" /> Schedule
            </Button>
          )}
          {hasPermission('action.export_email') && (
            <Button size="sm" variant="ghost" className="gap-1 text-[10px] text-muted-foreground" onClick={() => setShowScheduleList(!showScheduleList)} data-testid="view-schedules-btn">
              {schedules.length} active
            </Button>
          )}
          <Separator orientation="vertical" className="h-4" />
          {['coverage', 'brand', 'adoption', 'contracts'].includes(activeReport) && hasPermission('action.export_csv') && (
            <Button size="sm" variant="outline" className="gap-1.5 text-xs" onClick={() => {
              window.open(`${BACKEND_URL}/api/export/csv/${activeReport}`, '_blank');
            }} data-testid="export-csv">
              <Download className="h-3.5 w-3.5" /> CSV
            </Button>
          )}
          {hasPermission('action.export_email') && (
          <Button size="sm" variant="outline" className="gap-1.5 text-xs" onClick={() => setShowEmailDialog(true)} data-testid="email-report">
            <Mail className="h-3.5 w-3.5" /> Email
          </Button>
          )}
          {hasPermission('action.export_pdf') && (
          <Button size="sm" variant="outline" className="gap-1.5 text-xs" onClick={exportPdf} data-testid="export-report-pdf">
            <FileDown className="h-3.5 w-3.5" /> PDF
          </Button>
          )}
          </div>
        </div>

        {/* Schedule list panel */}
        {showScheduleList && (
          <div className="px-4 py-3 border-b bg-muted/20 space-y-2 shrink-0" data-testid="schedule-list-panel">
            <div className="flex items-center justify-between"><h4 className="text-xs font-semibold">Scheduled Reports ({schedules.length})</h4></div>
            {schedules.length === 0 ? <p className="text-xs text-muted-foreground py-2">No scheduled reports yet.</p> : (
              <div className="space-y-1">
                {schedules.map(s => (
                  <div key={s.id} className="flex items-center gap-2 p-2 rounded-lg border bg-card text-xs">
                    <Switch checked={s.enabled} onCheckedChange={(v) => toggleSchedule(s.id, v)} className="scale-75" />
                    <div className="flex-1 min-w-0">
                      <span className="font-medium truncate block">{s.name}</span>
                      <span className="text-[10px] text-muted-foreground">{(s.recipients || []).join(', ')} — {s.frequency}</span>
                    </div>
                    <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" onClick={() => deleteSchedule(s.id)}><AlertTriangle className="h-3 w-3 text-destructive" /></Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

      {/* Property Group Filter Bar */}
      {propertyGroups.length > 0 && (
        <div className="shrink-0 px-4 py-2 border-b bg-muted/20 flex items-center gap-2 overflow-x-auto" data-testid="report-group-filter">
          <Filter className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          <span className="text-[10px] text-muted-foreground font-medium shrink-0">Group:</span>
          <button
            onClick={() => { setSelectedGroupId(''); fetchReport(activeReport); }}
            className={`shrink-0 px-2.5 py-1 rounded-full text-[10px] font-medium transition-colors ${!selectedGroupId ? 'bg-primary text-primary-foreground' : 'bg-muted/60 text-muted-foreground hover:bg-muted'}`}
            data-testid="group-filter-all"
          >
            All Properties
          </button>
          {propertyGroups.map(g => (
            <button key={g.id}
              onClick={() => { setSelectedGroupId(g.id); }}
              className={`shrink-0 px-2.5 py-1 rounded-full text-[10px] font-medium transition-colors flex items-center gap-1 ${selectedGroupId === g.id ? 'bg-primary text-primary-foreground' : 'bg-muted/60 text-muted-foreground hover:bg-muted'}`}
              data-testid={`group-filter-${g.id}`}
            >
              {g.color && <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: g.color }} />}
              {g.name}
              <span className="opacity-60">({g.property_count})</span>
            </button>
          ))}
          {selectedGroupId && (
            <button onClick={() => { setSelectedGroupId(''); }} className="shrink-0 p-1 rounded-full hover:bg-muted/60" title="Clear filter">
              <X className="h-3 w-3 text-muted-foreground" />
            </button>
          )}
        </div>
      )}

      {/* Report content */}
      <ScrollArea className="flex-1">
        <div className="p-6 max-w-7xl mx-auto" id="report-content">
          {loading ? (
            <div className="py-20 text-center">
              <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">Loading report...</p>
            </div>
          ) : !canViewReport(activeReport) ? (
            <div className="py-20 text-center" data-testid="report-no-access">
              <Shield className="h-8 w-8 mx-auto text-muted-foreground/40 mb-3" />
              <p className="text-sm font-medium text-muted-foreground">Report Not Available</p>
              <p className="text-xs text-muted-foreground/70 mt-1">Your role does not have access to this report. Contact your admin to adjust permissions.</p>
            </div>
          ) : activeReport === 'compare' ? (
            <PropertyCompare
              allProperties={allProperties}
              compareProperties={compareProperties}
              setCompareProperties={setCompareProperties}
              data={data?.properties ? data : null}
              loading={loading}
              onCompare={fetchCompare}
            />
          ) : !data ? (
            <p className="text-sm text-muted-foreground text-center py-20">No data available.</p>
          ) : activeReport === 'executive' ? (
            <ExecutiveSummary data={data} />
          ) : activeReport === 'coverage' ? (
            <CoverageMatrix data={data} cellColor={cellColor} token={token} onRefresh={() => fetchReport('coverage')} />
          ) : activeReport === 'brand' ? (
            <BrandCompliance data={data} />
          ) : activeReport === 'adoption' ? (
            <SolutionAdoption data={data} />
          ) : activeReport === 'integration' ? (
            <IntegrationGaps data={data} />
          ) : activeReport === 'maturity' ? (
            <MaturityScorecard data={data} />
          ) : activeReport === 'vendors' ? (
            <VendorConcentration data={data} />
          ) : activeReport === 'vendorrisk' ? (
            <VendorRiskMap data={data} />
          ) : activeReport === 'standardization' ? (
            <StandardizationReport data={data} />
          ) : activeReport === 'renewals' ? (
            <RenewalCalendar data={data} />
          ) : activeReport === 'gaps' ? (
            <LayerGaps data={data} />
          ) : activeReport === 'costs' ? (
            <CostSummary data={data} />
          ) : activeReport === 'contracts' ? (
            <ContractsReport data={data} />
          ) : activeReport === 'scorecards' ? (
            <VendorScorecards data={data} />
          ) : activeReport === 'governance' ? (
            <GovernanceReport data={data} token={token} onRefresh={() => fetchReport('governance')} />
          ) : activeReport === 'credentials' ? (
            <CredentialAudit data={data} token={token} onRefresh={() => fetchReport('credentials')} />
          ) : activeReport === 'platformutil' ? (
            <SimpleTableReport title="Platform Utilization" desc="Adoption rate of each platform across properties." data={data?.platforms || []} columns={[{key:'name',label:'Platform'},{key:'property_count',label:'Properties'},{key:'utilization_pct',label:'Utilization',suffix:'%'}]} />
          ) : activeReport === 'solutiondrift' ? (
            <SimpleTableReport title="Solution Drift" desc="Retired or Not Approved solutions still deployed." data={data?.drifts || []} columns={[{key:'property_name',label:'Property'},{key:'platform_name',label:'Platform'},{key:'product_name',label:'Product'},{key:'status',label:'Status',badge:true}]} emptyMsg="No solution drift detected." />
          ) : activeReport === 'vendordiversity' ? (
            <SimpleTableReport title="Vendor Diversity" desc="Vendor concentration per property — lower = more single-vendor risk." data={data?.properties || []} columns={[{key:'property_name',label:'Property'},{key:'solution_count',label:'Solutions'},{key:'vendor_count',label:'Vendors'},{key:'diversity_pct',label:'Diversity',suffix:'%'}]} />
          ) : activeReport === 'techdebt' ? (
            <SimpleTableReport title="Tech Debt Index" desc="Solutions missing governance, SSO, contracts, or brand compliance." data={data?.properties || []} columns={[{key:'property_name',label:'Property'},{key:'solutions',label:'Solutions'},{key:'no_governance',label:'No Gov'},{key:'no_sso',label:'No SSO'},{key:'no_contract',label:'No Contract'},{key:'debt_score',label:'Debt Score',suffix:'%'}]} />
          ) : activeReport === 'corpvslocal' ? (
            <SimpleTableReport title="Corporate vs Local" desc="Ratio of centrally-managed vs property-managed solutions." data={data?.properties || []} columns={[{key:'property_name',label:'Property'},{key:'total',label:'Total'},{key:'corporate',label:'Corporate'},{key:'local',label:'Local'},{key:'corp_pct',label:'Corp %',suffix:'%'}]} />
          ) : activeReport === 'costheatmap' ? (
            <CostHeatmapReport data={data} />
          ) : activeReport === 'integflow' ? (
            <IntegFlowReport data={data} />
          ) : activeReport === 'onboarding' ? (
            <p className="text-sm text-muted-foreground text-center py-12">Select a specific property to view its onboarding readiness score.</p>
          ) : activeReport === 'esg' ? (
            <EsgReport data={data} />
          ) : activeReport === 'techage' ? (
            <SimpleTableReport title="Tech Stack Age" desc="Average solution tenure based on contract start dates." data={data?.properties || []} columns={[{key:'property_name',label:'Property'},{key:'solution_count',label:'Tracked'},{key:'avg_age_years',label:'Avg Years'},{key:'oldest',label:'Oldest (yrs)'}]} emptyMsg="No contract start dates entered." />
          ) : activeReport === 'platformgap' ? (
            <PlatformGapReport data={data} />
          ) : activeReport === 'multivendor' ? (
            <MultiVendorReport data={data} />
          ) : activeReport === 'ssodetail' ? (
            <SsoDetailReport data={data} token={token} onRefresh={() => fetchReport('ssodetail')} />
          ) : activeReport === 'brandgapdetail' ? (
            <BrandGapDetailReport data={data} />
          ) : activeReport === 'supportinfo' ? (
            <SupportCompletenessReport data={data} token={token} onRefresh={() => fetchReport('supportinfo')} />
          ) : activeReport === 'vendormap' ? (
            <VendorDeploymentMap data={data} />
          ) : activeReport === 'vendoradoption' ? (
            <VendorProductAdoption data={data} />
          ) : activeReport === 'vendorcontracts' ? (
            <SimpleTableReport title="Vendor Contract Summary" desc="Aggregate spend, contract count, and renewal dates per vendor." data={data?.vendors || []} columns={[{key:'vendor_name',label:'Vendor'},{key:'contract_count',label:'Contracts'},{key:'total_annual_spend',label:'Annual Spend',prefix:'$',comma:true},{key:'avg_per_contract',label:'Avg/Contract',prefix:'$',comma:true},{key:'property_count',label:'Properties'},{key:'next_renewal',label:'Next Renewal'}]} />
          ) : activeReport === 'vendoroverlap' ? (
            <VendorOverlapReport data={data} />
          ) : activeReport === 'brandscorecard' ? (
            <SimpleTableReport title="Brand Compliance Scorecard" desc="Per-brand compliance % across all properties." data={data?.brands || []} columns={[{key:'brand_name',label:'Brand'},{key:'required_count',label:'Required'},{key:'approved_count',label:'Approved'},{key:'compliant_properties',label:'Compliant Props'},{key:'total_properties',label:'Total Props'},{key:'compliance_pct',label:'Compliance',suffix:'%'}]} />
          ) : activeReport === 'brandmatrix' ? (
            <BrandPropertyMatrix data={data} />
          ) : activeReport === 'brandgaps' ? (
            <SimpleTableReport title="Brand Standard Gaps" desc="Required products not deployed at each property." data={data?.gaps || []} columns={[{key:'property_name',label:'Property'},{key:'brand_name',label:'Brand'},{key:'product_name',label:'Missing Product'}]} emptyMsg="No brand standard gaps found." />
          ) : activeReport === 'integtypeadv' ? (
            <IntegrationTypeAdvisory data={data} />
          ) : activeReport === 'propinventory' ? (
            <PropertySolutionInventory token={token} properties={allProperties} />
          ) : null}
        </div>
      </ScrollArea>
      </div> {/* end main content */}

      {/* Email Report Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Mail className="h-4 w-4" /> Email Report</DialogTitle>
            <DialogDescription>Send the {REPORT_TABS.find(t => t.id === activeReport)?.label} report to an email address.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Email Address</Label>
              <Input
                type="email"
                value={emailAddress}
                onChange={e => setEmailAddress(e.target.value)}
                placeholder="you@company.com"
                data-testid="email-report-input"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              The report will be emailed as a formatted HTML summary.
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowEmailDialog(false)}>Cancel</Button>
            <Button
              disabled={!emailAddress || emailing}
              data-testid="send-email-report"
              onClick={async () => {
                setEmailing(true);
                try {
                  const reportEl = document.getElementById('report-content');
                  const reportHtml = reportEl ? reportEl.innerHTML : '<p>Report data not available.</p>';
                  const tab = REPORT_TABS.find(t => t.id === activeReport);
                  await axios.post(`${BACKEND_URL}/api/admin/send-report-email`, {
                    recipient_email: emailAddress,
                    report_name: tab?.label || 'Report',
                    report_html: reportHtml,
                  }, { headers });
                  toast.success(`Report emailed to ${emailAddress}`);
                  setShowEmailDialog(false);
                  setEmailAddress('');
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Failed to send email');
                } finally {
                  setEmailing(false);
                }
              }}
            >
              {emailing ? 'Sending...' : 'Send Report'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule Report Dialog */}
      <Dialog open={showScheduleDialog} onOpenChange={setShowScheduleDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Clock className="h-4 w-4" /> Schedule Report</DialogTitle>
            <DialogDescription>Set up recurring delivery of the {REPORT_TABS.find(t => t.id === activeReport)?.label} report.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs">Recipients (comma-separated emails)</Label>
              <Input
                value={scheduleForm.recipients}
                onChange={e => setScheduleForm(p => ({ ...p, recipients: e.target.value }))}
                placeholder="cto@company.com, vp-tech@company.com"
                data-testid="schedule-recipients-input"
                className="mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Frequency</Label>
                <Select value={scheduleForm.frequency} onValueChange={v => setScheduleForm(p => ({ ...p, frequency: v }))}>
                  <SelectTrigger className="mt-1" data-testid="schedule-frequency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {scheduleForm.frequency === 'weekly' && (
                <div>
                  <Label className="text-xs">Day</Label>
                  <Select value={String(scheduleForm.day_of_week)} onValueChange={v => setScheduleForm(p => ({ ...p, day_of_week: parseInt(v) }))}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'].map((d, i) => (
                        <SelectItem key={i} value={String(i)}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowScheduleDialog(false)}>Cancel</Button>
            <Button onClick={createSchedule} disabled={scheduling || !scheduleForm.recipients} data-testid="create-schedule-btn" className="gap-1.5">
              {scheduling ? 'Creating...' : <><Clock className="h-3.5 w-3.5" /> Create Schedule</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SidebarLayout>
  );
}

function CoverageMatrix({ data, cellColor, token, onRefresh }) {
  const [fillGapsOpen, setFillGapsOpen] = useState(false);
  const [selectedProp, setSelectedProp] = useState(null);
  const properties = Array.isArray(data?.properties) ? data.properties : [];
  const categories = Array.isArray(data?.categories) ? data.categories : [];

  const handleRowClick = (prop) => {
    setSelectedProp(prop);
    setFillGapsOpen(true);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Property Tech Coverage Matrix</h2>
        <p className="text-xs text-muted-foreground">Click a property row to fill coverage gaps</p>
      </div>
      <p className="text-sm text-muted-foreground mb-4">Platforms deployed per category at each property.</p>
      <div className="overflow-x-auto border rounded-lg">
        <table className="w-full text-xs" data-testid="coverage-matrix-table">
          <thead className="bg-muted/40 sticky top-0">
            <tr>
              <th className="text-left p-2 font-semibold min-w-[160px] border-r">Property</th>
              {categories.map(c => (
                <th key={c.id} className="p-1.5 font-semibold text-center min-w-[60px] border-r" title={c.name}>
                  <div className="w-2 h-2 rounded-full mx-auto mb-0.5" style={{ backgroundColor: c.color }} />
                  <span className="text-[9px] leading-tight block">{c.name.split(' ')[0]}</span>
                </th>
              ))}
              <th className="p-2 font-semibold text-center min-w-[50px]">Total</th>
              <th className="p-2 font-semibold text-center min-w-[60px]">Action</th>
            </tr>
          </thead>
          <tbody>
            {properties.map(prop => {
              const totalPossible = categories.reduce((s, c) => s + (prop.categories[c.id]?.total || 0), 0);
              const gapCount = totalPossible - prop.total_used;
              return (
                <tr key={prop.property_id} className="border-t hover:bg-muted/20 cursor-pointer" onClick={() => handleRowClick(prop)}>
                  <td className="p-2 font-medium border-r truncate">{prop.property_name}</td>
                  {categories.map(c => {
                    const cell = prop.categories[c.id] || { used: 0, total: 0 };
                    return (
                      <td key={c.id} className={`p-1.5 text-center border-r ${cellColor(cell.used, cell.total)}`}>
                        {cell.used > 0 ? cell.used : '-'}
                      </td>
                    );
                  })}
                  <td className="p-2 text-center font-semibold">{prop.total_used}</td>
                  <td className="p-2 text-center">
                    {gapCount > 0 && (
                      <Button variant="ghost" size="sm" className="h-6 text-[10px] text-blue-600 hover:text-blue-700 px-2" data-testid={`fill-gaps-${prop.property_id}`} onClick={(e) => { e.stopPropagation(); handleRowClick(prop); }}>
                        +{gapCount} gaps
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {selectedProp && (
        <BulkFillGapsDialog
          open={fillGapsOpen}
          onOpenChange={setFillGapsOpen}
          token={token}
          propertyId={selectedProp.property_id}
          propertyName={selectedProp.property_name}
          onComplete={onRefresh}
        />
      )}
    </div>
  );
}

function BrandCompliance({ data }) {
  const properties = Array.isArray(data?.properties) ? data.properties : [];
  const brands = Array.isArray(data?.brands) ? data.brands : [];
  const cols = useColumnPicker('reports_brand_compliance', [
    { key: 'total', label: 'Total Solutions' },
    { key: 'required', label: 'Brand Required' },
    { key: 'approved', label: 'Brand Approved' },
    { key: 'non_brand', label: 'Non-Brand' },
    { key: 'compliance', label: 'Compliance %' },
  ]);
  return (
    <div>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="text-lg font-bold mb-1" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Brand Compliance Report</h2>
          <p className="text-sm text-muted-foreground">Solutions categorized by brand designation per property.</p>
        </div>
        <cols.Trigger />
      </div>
      <div className="overflow-x-auto border rounded-lg">
        <table className="w-full text-xs" data-testid="brand-compliance-table">
          <thead className="bg-muted/40">
            <tr>
              <th className="text-left p-2 font-semibold min-w-[160px]">Property</th>
              {cols.isVisible('total') && <th className="p-2 font-semibold text-center">Total Solutions</th>}
              {cols.isVisible('required') && <th className="p-2 font-semibold text-center"><span className="text-emerald-700">Brand Required</span></th>}
              {cols.isVisible('approved') && <th className="p-2 font-semibold text-center"><span className="text-blue-700">Brand Approved</span></th>}
              {cols.isVisible('non_brand') && <th className="p-2 font-semibold text-center"><span className="text-amber-700">Non-Brand</span></th>}
              {cols.isVisible('compliance') && <th className="p-2 font-semibold text-center">Compliance</th>}
            </tr>
          </thead>
          <tbody>
            {properties.filter(p => p.total > 0).map(prop => {
              const compliance = prop.total > 0 ? Math.round((prop.brand_required + prop.brand_approved) / prop.total * 100) : 0;
              return (
                <tr key={prop.property_id} className="border-t hover:bg-muted/20">
                  <td className="p-2 font-medium">{prop.property_name}</td>
                  {cols.isVisible('total') && <td className="p-2 text-center">{prop.total}</td>}
                  {cols.isVisible('required') && (
                    <td className="p-2 text-center">
                      {prop.brand_required > 0 && <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">{prop.brand_required}</Badge>}
                    </td>
                  )}
                  {cols.isVisible('approved') && (
                    <td className="p-2 text-center">
                      {prop.brand_approved > 0 && <Badge className="bg-blue-100 text-blue-700 text-[10px]">{prop.brand_approved}</Badge>}
                    </td>
                  )}
                  {cols.isVisible('non_brand') && (
                    <td className="p-2 text-center">
                      {prop.non_brand > 0 && <Badge className="bg-amber-100 text-amber-700 text-[10px]">{prop.non_brand}</Badge>}
                    </td>
                  )}
                  {cols.isVisible('compliance') && (
                    <td className="p-2 text-center">
                      <span className={`font-semibold ${compliance >= 80 ? 'text-emerald-600' : compliance >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                        {compliance}%
                      </span>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SolutionAdoption({ data }) {
  const top_solutions = data?.top_solutions || [];
  const maxCount = top_solutions.length > 0 ? top_solutions[0].deployment_count : 1;
  return (
    <div>
      <h2 className="text-lg font-bold mb-1" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Solution Adoption Dashboard</h2>
      <p className="text-sm text-muted-foreground mb-4">Most deployed solutions across all properties.</p>
      <div className="space-y-2" data-testid="solution-adoption-list">
        {top_solutions.map((s, i) => (
          <div key={s.product_id} className="flex items-center gap-3 p-3 rounded-lg border bg-card hover:bg-muted/20 transition-colors">
            <span className="text-xs font-bold text-muted-foreground w-6 text-right">#{i + 1}</span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold truncate">{s.product_name}</span>
                {s.status && (
                  <Badge variant="outline" className={`text-[10px] ${
                    s.status === 'Deployed' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
                  }`}>{s.status}</Badge>
                )}
              </div>
              <div className="text-xs text-muted-foreground truncate">{s.vendor_name} · {s.platform_name}</div>
            </div>
            <div className="w-32 shrink-0">
              <div className="h-2 rounded-full bg-muted overflow-hidden">
                <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${(s.deployment_count / maxCount) * 100}%` }} />
              </div>
            </div>
            <Badge variant="secondary" className="font-mono text-xs shrink-0">
              {s.deployment_count} {s.deployment_count === 1 ? 'deploy' : 'deploys'}
            </Badge>
          </div>
        ))}
      </div>
    </div>
  );
}

function VendorConcentration({ data }) {
  const properties = Array.isArray(data?.properties) ? data.properties : [];
  const top_vendors = Array.isArray(data?.top_vendors) ? data.top_vendors : [];
  const cols = useColumnPicker('reports_vendor_conc', [
    { key: 'vendors', label: 'Vendors' },
    { key: 'solutions', label: 'Solutions' },
    { key: 'concentration', label: 'Concentration' },
  ]);
  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-lg font-bold mb-1" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Vendor Concentration Analysis</h2>
        <p className="text-sm text-muted-foreground mb-4">Vendor dependency per property and top vendors by reach.</p>
      </div>

      <div>
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Top Vendors by Property Reach</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="top-vendors-grid">
          {top_vendors.slice(0, 12).map(v => (
            <div key={v.vendor_id} className="p-3 rounded-lg border bg-card text-center">
              <div className="text-lg font-bold text-primary">{v.property_count}</div>
              <div className="text-xs text-muted-foreground truncate">{v.vendor_name}</div>
              <div className="text-[10px] text-muted-foreground">properties</div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Vendor Count per Property</h3>
          <cols.Trigger />
        </div>
        <div className="overflow-x-auto border rounded-lg">
          <table className="w-full text-xs" data-testid="vendor-per-property-table">
            <thead className="bg-muted/40">
              <tr>
                <th className="text-left p-2 font-semibold">Property</th>
                {cols.isVisible('vendors') && <th className="p-2 font-semibold text-center">Vendors</th>}
                {cols.isVisible('solutions') && <th className="p-2 font-semibold text-center">Solutions</th>}
                {cols.isVisible('concentration') && <th className="p-2 font-semibold text-center">Concentration</th>}
              </tr>
            </thead>
            <tbody>
              {properties.filter(p => p.solution_count > 0).sort((a, b) => b.vendor_count - a.vendor_count).map(prop => (
                <tr key={prop.property_id} className="border-t hover:bg-muted/20">
                  <td className="p-2 font-medium">{prop.property_name}</td>
                  {cols.isVisible('vendors') && <td className="p-2 text-center">{prop.vendor_count}</td>}
                  {cols.isVisible('solutions') && <td className="p-2 text-center">{prop.solution_count}</td>}
                  {cols.isVisible('concentration') && (
                    <td className="p-2 text-center">
                      <span className={`font-semibold ${prop.vendor_count > 10 ? 'text-amber-600' : 'text-emerald-600'}`}>
                        {prop.vendor_count > 0 ? (prop.solution_count / prop.vendor_count).toFixed(1) : '-'} sol/vendor
                      </span>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function LayerGaps({ data }) {
  const properties = Array.isArray(data?.properties) ? data.properties : [];
  const cols = useColumnPicker('reports_layer_gaps', [
    { key: 'actual', label: 'Actual' },
    { key: 'ideal', label: 'Ideal' },
    { key: 'matches', label: 'Matches' },
    { key: 'actual_only', label: 'Actual Only' },
    { key: 'ideal_only', label: 'Ideal Only (Gaps)' },
    { key: 'gap_score', label: 'Gap Score' },
  ]);
  return (
    <div>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="text-lg font-bold mb-1" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Layer Gap Analysis</h2>
          <p className="text-sm text-muted-foreground">Actual vs Ideal layer comparison across all properties with ideal assignments.</p>
        </div>
        <cols.Trigger />
      </div>
      {properties.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-12">No properties have ideal layer assignments yet.</p>
      ) : (
        <div className="overflow-x-auto border rounded-lg">
          <table className="w-full text-xs" data-testid="layer-gaps-table">
            <thead className="bg-muted/40">
              <tr>
                <th className="text-left p-2 font-semibold min-w-[160px]">Property</th>
                {cols.isVisible('actual') && <th className="p-2 font-semibold text-center">Actual</th>}
                {cols.isVisible('ideal') && <th className="p-2 font-semibold text-center">Ideal</th>}
                {cols.isVisible('matches') && <th className="p-2 font-semibold text-center text-emerald-700">Matches</th>}
                {cols.isVisible('actual_only') && <th className="p-2 font-semibold text-center text-amber-700">Actual Only</th>}
                {cols.isVisible('ideal_only') && <th className="p-2 font-semibold text-center text-blue-700">Ideal Only (Gaps)</th>}
                {cols.isVisible('gap_score') && <th className="p-2 font-semibold text-center">Gap Score</th>}
              </tr>
            </thead>
            <tbody>
              {properties.sort((a, b) => a.gap_score - b.gap_score).map(prop => (
                <tr key={prop.property_id} className="border-t hover:bg-muted/20">
                  <td className="p-2 font-medium">{prop.property_name}</td>
                  {cols.isVisible('actual') && <td className="p-2 text-center">{prop.actual_count}</td>}
                  {cols.isVisible('ideal') && <td className="p-2 text-center">{prop.ideal_count}</td>}
                  {cols.isVisible('matches') && <td className="p-2 text-center text-emerald-600 font-medium">{prop.matches}</td>}
                  {cols.isVisible('actual_only') && <td className="p-2 text-center text-amber-600">{prop.actual_only}</td>}
                  {cols.isVisible('ideal_only') && <td className="p-2 text-center text-blue-600 font-medium">{prop.ideal_only}</td>}
                  {cols.isVisible('gap_score') && (
                    <td className="p-2 text-center">
                      <span className={`font-bold ${prop.gap_score >= 80 ? 'text-emerald-600' : prop.gap_score >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                        {prop.gap_score}%
                      </span>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function IntegrationTypeAdvisory({ data }) {
  const summary = data?.summary || {};
  const opportunities = data?.opportunities || [];
  const propertySummary = data?.property_summary || [];
  const TYPE_LABELS = { api: 'API / Direct', data_export: 'Data Export / File Drop', manual: 'Manual Process', hybrid: 'Hybrid' };
  const TYPE_COLORS = { api: 'bg-sky-100 text-sky-700', data_export: 'bg-amber-100 text-amber-700', manual: 'bg-slate-100 text-slate-600', hybrid: 'bg-purple-100 text-purple-700' };
  return (
    <div>
      <h2 className="text-lg font-bold mb-1" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Integration Type Advisory</h2>
      <p className="text-sm text-muted-foreground mb-4">Identifies where data exports or manual processes could be upgraded to direct API integrations.</p>
      {/* Summary cards */}
      <div className="grid grid-cols-4 gap-3 mb-6">
        <div className="p-3 rounded-lg border bg-card text-center">
          <div className="text-2xl font-bold text-primary">{summary.total_opportunities || 0}</div>
          <div className="text-[10px] text-muted-foreground uppercase">Total Opportunities</div>
        </div>
        <div className="p-3 rounded-lg border bg-amber-50 text-center">
          <div className="text-2xl font-bold text-amber-700">{summary.data_export_count || 0}</div>
          <div className="text-[10px] text-amber-600 uppercase">Data Exports</div>
        </div>
        <div className="p-3 rounded-lg border bg-slate-50 text-center">
          <div className="text-2xl font-bold text-slate-600">{summary.manual_count || 0}</div>
          <div className="text-[10px] text-slate-500 uppercase">Manual Processes</div>
        </div>
        <div className="p-3 rounded-lg border bg-emerald-50 text-center">
          <div className="text-2xl font-bold text-emerald-700">{summary.upgradeable_to_api || 0}</div>
          <div className="text-[10px] text-emerald-600 uppercase">API Upgrade Available</div>
        </div>
      </div>
      {opportunities.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-4xl mb-2">✓</div>
          <p className="text-sm text-muted-foreground">No integration upgrade opportunities found. All integrations are using optimal connection types.</p>
          <p className="text-xs text-muted-foreground mt-1">As you define more product integrations with types, opportunities will surface here.</p>
        </div>
      ) : (
        <>
          {/* Property summary */}
          {propertySummary.length > 0 && (
            <div className="mb-4">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Properties with Opportunities</h3>
              <div className="flex flex-wrap gap-2">
                {propertySummary.map(ps => (
                  <div key={ps.property_id} className="px-3 py-1.5 rounded-lg border bg-card text-xs">
                    <span className="font-medium">{ps.property_name}</span>
                    <span className="text-muted-foreground ml-1.5">
                      {ps.opportunity_count} opp{ps.opportunity_count !== 1 ? 's' : ''}
                      {ps.data_exports > 0 && <span className="text-amber-600 ml-1">{ps.data_exports} exports</span>}
                      {ps.manual > 0 && <span className="text-slate-500 ml-1">{ps.manual} manual</span>}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Detailed opportunities */}
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-xs" data-testid="integ-type-advisory-table">
              <thead><tr className="bg-muted/50">
                <th className="p-2 text-left font-semibold">Property</th>
                <th className="p-2 text-left font-semibold">Product A</th>
                <th className="p-2 text-left font-semibold">Product B</th>
                <th className="p-2 text-center font-semibold">Current Type</th>
                <th className="p-2 text-left font-semibold">Recommendation</th>
              </tr></thead>
              <tbody>
                {opportunities.map((opp, i) => (
                  <tr key={i} className="border-t hover:bg-muted/20">
                    <td className="p-2 font-medium">{opp.property_name}</td>
                    <td className="p-2">
                      <div>{opp.product_a_name}</div>
                      <div className="text-[10px] text-muted-foreground">{opp.product_a_platform}</div>
                    </td>
                    <td className="p-2">
                      <div>{opp.product_b_name}</div>
                      <div className="text-[10px] text-muted-foreground">{opp.product_b_platform}</div>
                    </td>
                    <td className="p-2 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[9px] font-medium ${TYPE_COLORS[opp.current_type] || ''}`}>
                        {TYPE_LABELS[opp.current_type] || opp.current_type}
                      </span>
                    </td>
                    <td className="p-2 text-muted-foreground max-w-[300px]">{opp.recommendation}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function IntegrationGaps({ data }) {
  const properties = Array.isArray(data?.properties) ? data.properties : [];
  return (
    <div>
      <h2 className="text-lg font-bold mb-1" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Integration Gap Analysis</h2>
      <p className="text-sm text-muted-foreground mb-4">Three-tier view: Platform Blueprint → Product Capability → Property Reality</p>
      <div className="flex gap-4 mb-4 text-xs flex-wrap">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-1.5 rounded bg-emerald-500" />
          Live (confirmed at property)
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-1.5 rounded bg-blue-500" />
          Available (product integration exists)
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-1.5 rounded bg-red-400" />
          Gap (no product integration)
        </div>
      </div>
      {properties.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-12">No integration data available. Add product-level integrations in the Tech Database to see coverage.</p>
      ) : (
        <div className="overflow-x-auto border rounded-lg">
          <table className="w-full text-xs" data-testid="integration-gaps-table">
            <thead className="bg-muted/40">
              <tr>
                <th className="text-left p-2 font-semibold min-w-[160px]">Property</th>
                <th className="p-2 font-semibold text-center">Blueprint</th>
                <th className="p-2 font-semibold text-center text-emerald-700">Live</th>
                <th className="p-2 font-semibold text-center text-blue-700">Available</th>
                <th className="p-2 font-semibold text-center text-red-600">Gaps</th>
                <th className="p-2 font-semibold text-center">Coverage</th>
              </tr>
            </thead>
            <tbody>
              {[...properties].sort((a, b) => a.coverage - b.coverage).map(prop => (
                <tr key={prop.property_id} className="border-t hover:bg-muted/20">
                  <td className="p-2 font-medium">{prop.property_name}</td>
                  <td className="p-2 text-center">{prop.total_template}</td>
                  <td className="p-2 text-center text-emerald-600 font-medium">{prop.live ?? prop.active ?? 0}</td>
                  <td className="p-2 text-center text-blue-600">{prop.available ?? prop.possible ?? 0}</td>
                  <td className="p-2 text-center text-red-600 font-medium">{prop.gaps}</td>
                  <td className="p-2 text-center">
                    <div className="flex items-center gap-2 justify-center">
                      <div className="w-16 h-2 rounded-full bg-muted overflow-hidden">
                        <div className="h-full rounded-full bg-emerald-500" style={{ width: `${prop.coverage}%` }} />
                      </div>
                      <span className={`font-bold ${prop.coverage >= 50 ? 'text-emerald-600' : prop.coverage > 0 ? 'text-amber-600' : 'text-red-600'}`}>
                        {prop.coverage}%
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function PropertyCompare({ allProperties, compareProperties, setCompareProperties, data, loading, onCompare }) {
  const [expandedCats, setExpandedCats] = useState({});
  const [hoveredCell, setHoveredCell] = useState(null); // {propIdx, catId}

  const toggleProperty = (pid) => {
    setCompareProperties(prev => {
      if (prev.includes(pid)) return prev.filter(id => id !== pid);
      if (prev.length >= 6) return prev;
      return [...prev, pid];
    });
  };

  const properties = Array.isArray(data?.properties) ? data.properties : [];
  const categories = Array.isArray(data?.categories) ? data.categories : [];
  const colWidth = properties.length > 0 ? Math.max(160, Math.floor(700 / properties.length)) : 200;

  return (
    <div data-testid="property-compare">
      <h2 className="text-lg font-bold mb-1" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Property Comparison</h2>
      <p className="text-sm text-muted-foreground mb-4">Select 2-6 properties to compare side by side.</p>

      {/* Clean property selector */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Select onValueChange={(v) => { if (!compareProperties.includes(v)) toggleProperty(v); }}>
          <SelectTrigger className="w-[260px] h-9"><SelectValue placeholder="Add a property to compare..." /></SelectTrigger>
          <SelectContent>
            {allProperties.filter(p => !compareProperties.includes(p.id)).map(p => (
              <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {compareProperties.map(pid => {
          const prop = allProperties.find(p => p.id === pid);
          return (
            <Badge key={pid} className="text-xs gap-1 bg-primary text-primary-foreground py-1 pl-2.5 pr-1 cursor-pointer" onClick={() => toggleProperty(pid)}>
              {prop?.name || pid}
              <span className="ml-1 hover:bg-white/20 rounded-full px-0.5">×</span>
            </Badge>
          );
        })}
        {compareProperties.length >= 2 && (
          <Button size="sm" className="gap-1.5 h-8" onClick={onCompare} disabled={loading} data-testid="run-compare">
            <GitCompareArrows className="h-3.5 w-3.5" />
            {loading ? 'Comparing...' : 'Compare'}
          </Button>
        )}
      </div>
      {compareProperties.length < 2 && <p className="text-xs text-muted-foreground mb-6">Select at least 2 properties to compare.</p>}

      {/* Comparison results */}
      {properties.length > 0 && properties[0]?.solutions !== undefined && (
        <div className="border rounded-lg overflow-x-auto">
          <table className="w-full text-xs" style={{ tableLayout: 'fixed' }} data-testid="compare-table">
            <colgroup>
              <col style={{ width: '180px' }} />
              {properties.map(p => <col key={p.property_id} style={{ width: `${colWidth}px` }} />)}
            </colgroup>

            {/* Header with property names */}
            <thead className="bg-muted/40 sticky top-0 z-10">
              <tr>
                <th className="text-left p-2.5 font-semibold border-r bg-muted/40">Metric</th>
                {properties.map(p => (
                  <th key={p.property_id} className="p-2.5 font-semibold text-center border-r">{p.property_name}</th>
                ))}
              </tr>
            </thead>

            <tbody>
              {/* KPI rows */}
              {[
                { label: 'Solutions Deployed', key: 'solutions_deployed', highlight: true },
                { label: 'Vendors', key: 'vendor_count' },
                { label: 'Brand Compliance', key: 'brand_compliance_pct', suffix: '%', colorFn: v => v >= 50 ? 'text-emerald-600' : 'text-amber-600' },
                { label: 'Unique Platforms', key: 'unique_count' },
                { label: 'Shared Platforms', key: 'shared_count', bg: 'bg-muted/20' },
              ].map(row => {
                const values = properties.map(p => p[row.key] || 0);
                const max = Math.max(...values);
                return (
                  <tr key={row.key} className={`border-t ${row.bg || ''}`}>
                    <td className="p-2.5 font-medium border-r">{row.label}</td>
                    {properties.map((p, i) => (
                      <td key={p.property_id} className={`p-2.5 text-center font-semibold border-r ${row.colorFn ? row.colorFn(values[i]) : (row.highlight && values[i] === max ? 'text-emerald-600' : '')}`}>
                        {values[i]}{row.suffix || ''}
                      </td>
                    ))}
                  </tr>
                );
              })}

              {/* Category rows with expandable products */}
              {categories.map(cat => {
                const isExpanded = expandedCats[cat.id] || false;
                return (
                  <React.Fragment key={cat.id}>
                    <tr className="border-t bg-muted/10 cursor-pointer hover:bg-muted/20" onClick={() => setExpandedCats(prev => ({ ...prev, [cat.id]: !isExpanded }))}>
                      <td className="p-2.5 font-medium border-r">
                        <div className="flex items-center gap-1.5">
                          <ChevronDown className={`h-3 w-3 text-muted-foreground transition-transform ${isExpanded ? '' : '-rotate-90'}`} />
                          <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: cat.color }} />
                          {cat.name}
                        </div>
                      </td>
                      {properties.map((p, pi) => {
                        const count = p.category_coverage?.[cat.id] || 0;
                        const products = p.solutions_by_category?.[cat.id] || [];
                        return (
                          <td key={p.property_id}
                            className={`p-2.5 text-center font-semibold border-r relative ${count > 0 ? '' : 'text-muted-foreground/40'}`}
                            onMouseEnter={() => count > 0 && setHoveredCell({ propIdx: pi, catId: cat.id })}
                            onMouseLeave={() => setHoveredCell(null)}
                          >
                            {count > 0 ? count : '—'}
                            {/* Hover tooltip */}
                            {hoveredCell?.propIdx === pi && hoveredCell?.catId === cat.id && products.length > 0 && (
                              <div className="absolute z-20 bg-card border rounded-lg shadow-xl p-2.5 text-left min-w-[180px] -translate-x-1/2 left-1/2 top-full mt-1" onClick={e => e.stopPropagation()}>
                                <p className="text-[9px] uppercase tracking-wider text-muted-foreground font-bold mb-1">{cat.name}</p>
                                {products.map((s, i) => (
                                  <div key={i} className="text-xs py-0.5 flex items-center gap-1.5">
                                    <div className="w-1 h-1 rounded-full bg-emerald-500 shrink-0" />
                                    <span className="font-medium">{s.platform_name}</span>
                                    {s.product_name && <span className="text-muted-foreground">— {s.product_name}</span>}
                                  </div>
                                ))}
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                    {/* Expanded product rows */}
                    {isExpanded && (() => {
                      // Collect all unique platform_ids across compared properties for this category
                      const allPlatIds = new Set();
                      properties.forEach(p => (p.solutions_by_category?.[cat.id] || []).forEach(s => allPlatIds.add(s.platform_id)));
                      return Array.from(allPlatIds).map(platId => {
                        const firstName = properties.reduce((acc, p) => {
                          if (acc) return acc;
                          const s = (p.solutions_by_category?.[cat.id] || []).find(x => x.platform_id === platId);
                          return s?.platform_name || '';
                        }, '');
                        return (
                          <tr key={`${cat.id}-${platId}`} className="border-t">
                            <td className="p-2 pl-8 text-[10px] text-muted-foreground border-r">{firstName}</td>
                            {properties.map(p => {
                              const sol = (p.solutions_by_category?.[cat.id] || []).find(s => s.platform_id === platId);
                              return (
                                <td key={p.property_id} className={`p-2 text-[10px] text-center border-r ${sol ? '' : 'text-muted-foreground/30'}`}>
                                  {sol ? (sol.product_name || 'Assigned') : '—'}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      });
                    })()}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function CostSummary({ data }) {
  const totalSpend = data?.total_spend || 0;
  const byProperty = data?.by_property || [];
  const byCategory = data?.by_category || [];
  const fmt = (n) => n ? `$${n.toLocaleString()}` : '$0';

  return (
    <div>
      <h2 className="text-lg font-bold mb-1" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Cost Summary</h2>
      <p className="text-sm text-muted-foreground mb-4">Annual technology spend across the portfolio.</p>
      <div className="text-3xl font-bold text-primary mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{fmt(totalSpend)} <span className="text-sm font-normal text-muted-foreground">total annual spend</span></div>
      {totalSpend === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8">No cost data entered yet. Add costs to assignments in the Tech Database.</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="border rounded-lg p-4">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">By Property</h3>
            <div className="space-y-2">
              {byProperty.slice(0, 15).map(p => (
                <div key={p.name} className="flex items-center justify-between text-sm">
                  <span>{p.name}</span><span className="font-mono font-semibold">{fmt(p.cost)}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="border rounded-lg p-4">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">By Category</h3>
            <div className="space-y-2">
              {byCategory.map(c => (
                <div key={c.name} className="flex items-center justify-between text-sm">
                  <span>{c.name}</span><span className="font-mono font-semibold">{fmt(c.cost)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ContractsReport({ data }) {
  const contracts = data?.contracts || [];
  return (
    <div>
      <h2 className="text-lg font-bold mb-1" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Contracts</h2>
      <p className="text-sm text-muted-foreground mb-4">{contracts.length} contracts tracked.</p>
      {contracts.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8">No contracts entered yet. Add contracts from the Product Detail dialog.</p>
      ) : (
        <div className="overflow-x-auto border rounded-lg">
          <table className="w-full text-xs" data-testid="contracts-table">
            <thead className="bg-muted/40">
              <tr>
                <th className="text-left p-2 font-semibold">Product</th>
                <th className="text-left p-2 font-semibold">Property</th>
                <th className="text-left p-2 font-semibold">Vendor</th>
                <th className="p-2 font-semibold text-center">Renewal</th>
                <th className="p-2 font-semibold text-center">Annual Value</th>
                <th className="p-2 font-semibold text-center">Auto-Renew</th>
              </tr>
            </thead>
            <tbody>
              {contracts.map((c, i) => (
                <tr key={i} className="border-t hover:bg-muted/20">
                  <td className="p-2 font-medium">{c.product_name}</td>
                  <td className="p-2">{c.property_name}</td>
                  <td className="p-2">{c.vendor_name}</td>
                  <td className="p-2 text-center">{c.renewal_date || '-'}</td>
                  <td className="p-2 text-center font-mono">{c.annual_value ? `$${c.annual_value.toLocaleString()}` : '-'}</td>
                  <td className="p-2 text-center">{c.auto_renew ? 'Yes' : 'No'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function VendorScorecards({ data }) {
  const scorecards = data?.scorecards || [];
  const dims = ['support_quality', 'integration_ease', 'value_for_money', 'reliability', 'innovation'];
  const dimLabels = { support_quality: 'Support', integration_ease: 'Integration', value_for_money: 'Value', reliability: 'Reliability', innovation: 'Innovation' };

  return (
    <div>
      <h2 className="text-lg font-bold mb-1" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Vendor Scorecards</h2>
      <p className="text-sm text-muted-foreground mb-4">Vendor ratings across key dimensions (1-5 scale).</p>
      {scorecards.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8">No vendor scores entered yet. Rate vendors from the user menu &gt; Admin Panel.</p>
      ) : (
        <div className="space-y-3" data-testid="vendor-scorecards-list">
          {scorecards.map(s => (
            <div key={s.vendor_id} className="border rounded-lg p-4 bg-card">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <span className="text-sm font-semibold">{s.vendor_name}</span>
                  <span className="text-xs text-muted-foreground ml-2">{s.product_count} products</span>
                </div>
                <Badge variant="secondary" className="text-sm font-bold">{s.average_score}/5</Badge>
              </div>
              <div className="grid grid-cols-5 gap-2">
                {dims.map(d => (
                  <div key={d} className="text-center">
                    <div className="text-lg font-bold" style={{ color: s[d] >= 4 ? '#10B981' : s[d] >= 3 ? '#F59E0B' : '#EF4444' }}>
                      {s[d] || '-'}
                    </div>
                    <div className="text-[10px] text-muted-foreground">{dimLabels[d]}</div>
                  </div>
                ))}
              </div>
              {s.notes && <p className="text-xs text-muted-foreground mt-2">{s.notes}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function GovernanceReport({ data, token, onRefresh }) {
  const [bulkGovOpen, setBulkGovOpen] = useState(false);
  const [bulkContactOpen, setBulkContactOpen] = useState(false);
  const rows = data?.assignments || [];
  const summary = data?.summary || {};
  const noGovItems = data?.no_governance_items || [];
  const ownerColors = { corporate: 'bg-blue-100 text-blue-700', property: 'bg-emerald-100 text-emerald-700', third_party: 'bg-amber-100 text-amber-700', brand: 'bg-purple-100 text-purple-700', other: 'bg-gray-100 text-gray-600', unassigned: 'bg-red-50 text-red-400' };

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Technology Governance</h2>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="gap-1 text-xs" onClick={() => setBulkGovOpen(true)} disabled={noGovItems.length === 0} data-testid="gov-bulk-set-btn">
            <Shield className="h-3 w-3" /> Bulk Set Governance ({noGovItems.length})
          </Button>
          <Button variant="outline" size="sm" className="gap-1 text-xs" onClick={() => setBulkContactOpen(true)} disabled={noGovItems.length === 0}>
            <Headphones className="h-3 w-3" /> Bulk Add Contacts ({noGovItems.length})
          </Button>
        </div>
      </div>
      <p className="text-sm text-muted-foreground mb-6">Who owns, manages, and supports each solution.</p>
      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
        {['platform_admin', 'credential_admin', 'daily_management', 'billing_owner', 'tech_support'].map(field => (
          <div key={field} className="border rounded-lg p-3">
            <h4 className="text-[10px] font-semibold text-muted-foreground uppercase mb-2">{field.replace(/_/g, ' ')}</h4>
            <div className="space-y-1">
              {Object.entries(summary[field] || {}).sort((a,b) => b[1] - a[1]).map(([owner, count]) => (
                <div key={owner} className="flex items-center justify-between">
                  <Badge className={`text-[9px] ${ownerColors[owner] || ''}`}>{owner}</Badge>
                  <span className="text-xs font-mono">{count}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8">No governance data entered yet. Set governance on solution assignments.</p>
      ) : (
        <div className="overflow-x-auto border rounded-lg">
          <table className="w-full text-xs" data-testid="governance-table">
            <thead className="bg-muted/40">
              <tr>
                <th className="text-left p-2 font-semibold">Property</th>
                <th className="text-left p-2 font-semibold">Platform</th>
                <th className="text-left p-2 font-semibold">Product</th>
                <th className="p-2 font-semibold text-center">Admin</th>
                <th className="p-2 font-semibold text-center">Credentials</th>
                <th className="p-2 font-semibold text-center">Daily Mgmt</th>
                <th className="p-2 font-semibold text-center">Billing</th>
                <th className="p-2 font-semibold text-center">Support</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-t hover:bg-muted/20">
                  <td className="p-2">{r.property_name}</td>
                  <td className="p-2">{r.platform_name}</td>
                  <td className="p-2 font-medium">{r.product_name}</td>
                  {['platform_admin', 'credential_admin', 'daily_management', 'billing_owner', 'tech_support'].map(f => (
                    <td key={f} className="p-2 text-center">{r[f] ? <Badge className={`text-[9px] ${ownerColors[r[f]] || ''}`}>{r[f]}</Badge> : '-'}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <BulkGovernanceDialog open={bulkGovOpen} onOpenChange={setBulkGovOpen} token={token} items={noGovItems} onComplete={onRefresh} />
      <BulkSupportContactDialog open={bulkContactOpen} onOpenChange={setBulkContactOpen} token={token} items={noGovItems} onComplete={onRefresh} />
    </div>
  );
}

function CredentialAudit({ data, token, onRefresh }) {
  const summary = data?.summary || {};
  const details = data?.details || [];
  return (
    <div>
      <h2 className="text-lg font-bold mb-1" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Credential & SSO Audit</h2>
      <p className="text-sm text-muted-foreground mb-6">Authentication methods and SSO coverage across the portfolio.</p>
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
        <div className="p-4 rounded-xl border bg-card text-center">
          <div className="text-2xl font-bold text-emerald-600" style={{ fontFamily: 'Space Grotesk' }}>{summary.sso_coverage_pct || 0}%</div>
          <div className="text-[10px] text-muted-foreground">SSO Coverage</div>
        </div>
        <div className="p-4 rounded-xl border bg-card text-center">
          <div className="text-2xl font-bold text-blue-600" style={{ fontFamily: 'Space Grotesk' }}>{summary.sso || 0}</div>
          <div className="text-[10px] text-muted-foreground">SSO</div>
        </div>
        <div className="p-4 rounded-xl border bg-card text-center">
          <div className="text-2xl font-bold text-amber-600" style={{ fontFamily: 'Space Grotesk' }}>{summary.standalone || 0}</div>
          <div className="text-[10px] text-muted-foreground">Standalone</div>
        </div>
        <div className="p-4 rounded-xl border bg-card text-center">
          <div className="text-2xl font-bold text-red-500" style={{ fontFamily: 'Space Grotesk' }}>{summary.shared || 0}</div>
          <div className="text-[10px] text-muted-foreground">Shared Creds</div>
        </div>
        <div className="p-4 rounded-xl border bg-card text-center">
          <div className="text-2xl font-bold text-emerald-600" style={{ fontFamily: 'Space Grotesk' }}>{summary.mfa_enabled || 0}</div>
          <div className="text-[10px] text-muted-foreground">MFA Enabled</div>
        </div>
      </div>
      {details.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8">No credential data entered yet. Set auth methods in governance settings.</p>
      ) : (
        <div className="overflow-x-auto border rounded-lg">
          <table className="w-full text-xs" data-testid="credential-audit-table">
            <thead className="bg-muted/40">
              <tr>
                <th className="text-left p-2 font-semibold">Property</th>
                <th className="text-left p-2 font-semibold">Platform</th>
                <th className="p-2 font-semibold text-center">Auth Method</th>
                <th className="p-2 font-semibold text-center">SSO Provider</th>
                <th className="p-2 font-semibold text-center">MFA</th>
              </tr>
            </thead>
            <tbody>
              {details.map((r, i) => (
                <tr key={i} className="border-t hover:bg-muted/20">
                  <td className="p-2">{r.property_name}</td>
                  <td className="p-2">{r.platform_name}</td>
                  <td className="p-2 text-center"><Badge variant="outline" className={`text-[9px] ${r.auth_method === 'sso' ? 'text-emerald-700 border-emerald-300' : r.auth_method === 'shared' ? 'text-red-600 border-red-300' : ''}`}>{r.auth_method}</Badge></td>
                  <td className="p-2 text-center">{r.sso_provider || '-'}</td>
                  <td className="p-2 text-center">{r.mfa_required ? <Badge className="text-[9px] bg-emerald-100 text-emerald-700">Yes</Badge> : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function ExecutiveSummary({ data }) {
  const fmt = (n) => n ? `$${n.toLocaleString()}` : '$0';
  return (
    <div>
      <h2 className="text-lg font-bold mb-1" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Executive Summary</h2>
      <p className="text-sm text-muted-foreground mb-6">Portfolio-wide technology overview for leadership meetings.</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Properties', value: data.properties, color: 'text-indigo-600' },
          { label: 'Solutions Deployed', value: data.total_solutions_deployed, color: 'text-blue-600' },
          { label: 'Avg per Property', value: data.avg_solutions_per_property, color: 'text-teal-600' },
          { label: 'Products Catalogued', value: data.products_catalogued, color: 'text-emerald-600' },
          { label: 'Vendors Tracked', value: data.vendors_tracked, color: 'text-amber-600' },
          { label: 'Annual Tech Spend', value: fmt(data.total_annual_spend), color: 'text-red-600' },
          { label: 'Renewals (90 days)', value: data.upcoming_renewals_90d, color: 'text-orange-600' },
          { label: 'Brand Compliant', value: data.brand_compliant_solutions, color: 'text-purple-600' },
        ].map((kpi, i) => (
          <div key={i} className="p-4 rounded-xl border bg-card text-center">
            <div className={`text-2xl font-bold ${kpi.color}`} style={{ fontFamily: 'Space Grotesk' }}>{kpi.value}</div>
            <div className="text-[10px] text-muted-foreground mt-1">{kpi.label}</div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="p-4 rounded-xl border"><h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">SSO Enabled</h4><div className="text-3xl font-bold text-emerald-600" style={{ fontFamily: 'Space Grotesk' }}>{data.sso_enabled}</div></div>
        <div className="p-4 rounded-xl border"><h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">Governance Tracked</h4><div className="text-3xl font-bold text-blue-600" style={{ fontFamily: 'Space Grotesk' }}>{data.governance_tracked}</div></div>
      </div>
    </div>
  );
}

function MaturityScorecard({ data }) {
  const scorecards = data?.scorecards || [];
  const dims = ['integration_score', 'compliance_score', 'sso_score', 'governance_score', 'contract_score'];
  const labels = { integration_score: 'Integration', compliance_score: 'Brand', sso_score: 'SSO', governance_score: 'Governance', contract_score: 'Contracts' };
  return (
    <div>
      <h2 className="text-lg font-bold mb-1" style={{ fontFamily: 'Space Grotesk' }}>Technology Maturity Scorecard</h2>
      <p className="text-sm text-muted-foreground mb-6">Five-dimension maturity rating per property (0-5 scale).</p>
      {scorecards.length === 0 ? <p className="text-sm text-muted-foreground text-center py-8">No data. Add governance, SSO, and contract info to see scores.</p> : (
        <div className="space-y-3">
          {scorecards.map(s => (
            <div key={s.property_id} className="border rounded-lg p-4 bg-card">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-semibold">{s.property_name}</span>
                <Badge variant="secondary" className={`text-sm font-bold ${s.overall_score >= 3 ? 'bg-emerald-100 text-emerald-700' : s.overall_score >= 1.5 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>{s.overall_score}/5</Badge>
              </div>
              <div className="grid grid-cols-5 gap-2">
                {dims.map(d => (
                  <div key={d} className="text-center">
                    <div className="text-lg font-bold" style={{ color: s[d] >= 4 ? '#10B981' : s[d] >= 2 ? '#F59E0B' : '#EF4444' }}>{s[d]}</div>
                    <div className="text-[9px] text-muted-foreground">{labels[d]}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function VendorRiskMap({ data }) {
  const vendors = data?.vendors || [];
  const maxProps = vendors.length > 0 ? vendors[0].property_count : 1;
  return (
    <div>
      <h2 className="text-lg font-bold mb-1" style={{ fontFamily: 'Space Grotesk' }}>Vendor Dependency Risk Map</h2>
      <p className="text-sm text-muted-foreground mb-6">Vendors ranked by portfolio exposure. Larger = more properties dependent. Color = scorecard rating.</p>
      <div className="flex flex-wrap gap-3 mb-6">
        {vendors.slice(0, 20).map(v => {
          const size = Math.max(60, (v.property_count / maxProps) * 140);
          const bg = v.risk_level === 'high' ? 'bg-red-100 border-red-300' : v.risk_level === 'medium' ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200';
          return (
            <div key={v.vendor_id} className={`rounded-xl border flex flex-col items-center justify-center p-2 ${bg}`} style={{ width: size, height: size }}>
              <span className="text-xs font-bold text-center leading-tight">{v.vendor_name.split(' ')[0]}</span>
              <span className="text-[10px] text-muted-foreground">{v.property_count} props</span>
              {v.average_score > 0 && <span className="text-[9px] font-mono">{v.average_score}/5</span>}
            </div>
          );
        })}
      </div>
      <div className="flex gap-4 text-xs text-muted-foreground">
        <span className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-red-200" /> High risk</span>
        <span className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-amber-200" /> Medium</span>
        <span className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-emerald-200" /> Low</span>
      </div>
    </div>
  );
}

function StandardizationReport({ data }) {
  const opps = data?.opportunities || [];
  return (
    <div>
      <h2 className="text-lg font-bold mb-1" style={{ fontFamily: 'Space Grotesk' }}>Standardization Opportunities</h2>
      <p className="text-sm text-muted-foreground mb-6">Platforms with multiple different vendors — consolidation candidates.</p>
      {opps.length === 0 ? <p className="text-sm text-muted-foreground text-center py-8">No fragmentation detected. All platforms use single vendors.</p> : (
        <div className="space-y-3">
          {opps.map(o => (
            <div key={o.platform_id} className={`border rounded-lg p-4 bg-card ${o.fragmentation === 'high' ? 'border-l-4 border-l-red-400' : 'border-l-4 border-l-amber-300'}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold">{o.platform_name}</span>
                <div className="flex gap-2">
                  <Badge variant="outline" className="text-[10px]">{o.vendor_count} vendors</Badge>
                  <Badge variant="outline" className="text-[10px]">{o.property_count} properties</Badge>
                  <Badge className={`text-[10px] ${o.fragmentation === 'high' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{o.fragmentation}</Badge>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {o.vendors.map(v => <Badge key={v} variant="secondary" className="text-[10px]">{v}</Badge>)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RenewalCalendar({ data }) {
  const events = data?.events || [];
  const urgencyColor = { critical: 'bg-red-100 border-red-300 text-red-700', warning: 'bg-amber-50 border-amber-200 text-amber-700', ok: 'bg-card border-border' };
  return (
    <div>
      <h2 className="text-lg font-bold mb-1" style={{ fontFamily: 'Space Grotesk' }}>Renewal Calendar</h2>
      <p className="text-sm text-muted-foreground mb-6">{events.length} upcoming contract renewals.</p>
      {events.length === 0 ? <p className="text-sm text-muted-foreground text-center py-8">No renewal dates tracked. Add renewal dates to contracts.</p> : (
        <div className="space-y-2">
          {events.map((e, i) => (
            <div key={i} className={`flex items-center gap-3 p-3 rounded-lg border ${urgencyColor[e.urgency] || urgencyColor.ok}`}>
              <div className="w-16 text-center shrink-0">
                <div className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>{e.days_until >= 0 ? e.days_until : 'Past'}</div>
                <div className="text-[9px] text-muted-foreground">{e.days_until >= 0 ? 'days' : 'due'}</div>
              </div>
              <Separator orientation="vertical" className="h-8" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">{e.product_name}</div>
                <div className="text-xs text-muted-foreground">{e.property_name} · Renews {e.renewal_date}</div>
              </div>
              {e.annual_value > 0 && <Badge variant="secondary" className="font-mono text-xs">${e.annual_value.toLocaleString()}/yr</Badge>}
              {e.auto_renew && <Badge className="text-[9px] bg-emerald-100 text-emerald-700">Auto</Badge>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function SimpleTableReport({ title, desc, data, columns, emptyMsg }) {
  return (
    <div>
      <h2 className="text-lg font-bold mb-1" style={{ fontFamily: 'Space Grotesk' }}>{title}</h2>
      <p className="text-sm text-muted-foreground mb-6">{desc}</p>
      {data.length === 0 ? <p className="text-sm text-muted-foreground text-center py-8">{emptyMsg || 'No data available.'}</p> : (
        <div className="overflow-x-auto border rounded-lg">
          <table className="w-full text-xs">
            <thead className="bg-muted/40">
              <tr>{columns.map(c => <th key={c.key} className="text-left p-2.5 font-semibold">{c.label}</th>)}</tr>
            </thead>
            <tbody>
              {data.slice(0, 50).map((row, i) => (
                <tr key={i} className="border-t hover:bg-muted/20">
                  {columns.map(c => (
                    <td key={c.key} className="p-2.5">
                      {c.badge ? <Badge variant="outline" className={`text-[10px] ${row[c.key] === 'Retired' ? 'text-slate-500' : row[c.key] === 'Not Approved' ? 'text-red-600' : ''}`}>{row[c.key]}</Badge> :
                       c.suffix ? <span className="font-mono">{c.prefix || ''}{c.comma ? (row[c.key] || 0).toLocaleString() : row[c.key]}{c.suffix}</span> :
                       c.prefix ? <span className="font-mono">{c.prefix}{c.comma ? (row[c.key] || 0).toLocaleString() : row[c.key]}</span> :
                       row[c.key]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CostHeatmapReport({ data }) {
  const rows = data?.rows || [];
  const categories = data?.categories || [];
  return (
    <div>
      <h2 className="text-lg font-bold mb-1" style={{ fontFamily: 'Space Grotesk' }}>Cost Heatmap</h2>
      <p className="text-sm text-muted-foreground mb-6">Annual spend by property and category. Darker = higher spend.</p>
      {rows.length === 0 ? <p className="text-sm text-muted-foreground text-center py-8">No cost data. Add costs to assignments.</p> : (
        <div className="overflow-x-auto border rounded-lg">
          <table className="w-full text-xs">
            <thead className="bg-muted/40">
              <tr>
                <th className="text-left p-2 font-semibold">Property</th>
                {categories.map(c => <th key={c.id} className="p-2 font-semibold text-center text-[9px]">{c.name.split(' ')[0]}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-t">
                  <td className="p-2 font-medium">{r.property}</td>
                  {categories.map(c => {
                    const cost = r.costs?.[c.id] || 0;
                    const intensity = Math.min(cost / 50000, 1);
                    return <td key={c.id} className="p-2 text-center" style={{ backgroundColor: cost > 0 ? `rgba(239,68,68,${intensity * 0.3 + 0.05})` : '' }}>
                      {cost > 0 ? `$${Math.round(cost / 1000)}k` : '-'}
                    </td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function IntegFlowReport({ data }) {
  const nodes = data?.nodes || [];
  const links = data?.links || [];
  return (
    <div>
      <h2 className="text-lg font-bold mb-1" style={{ fontFamily: 'Space Grotesk' }}>Integration Data Flow</h2>
      <p className="text-sm text-muted-foreground mb-6">How data moves between platforms. {nodes.length} nodes, {links.length} connections.</p>
      {nodes.length === 0 ? <p className="text-sm text-muted-foreground text-center py-8">No integration data available.</p> : (
        <div className="space-y-2">
          {links.map((l, i) => {
            const srcNode = nodes.find(n => n.id === l.source);
            const tgtNode = nodes.find(n => n.id === l.target);
            return (
              <div key={i} className="flex items-center gap-3 text-sm p-2 rounded-lg bg-muted/30">
                <Badge style={{ backgroundColor: srcNode?.color + '20', color: srcNode?.color }} className="text-[10px]">{srcNode?.name}</Badge>
                <ArrowRight className="h-3 w-3 text-muted-foreground" />
                <Badge style={{ backgroundColor: tgtNode?.color + '20', color: tgtNode?.color }} className="text-[10px]">{tgtNode?.name}</Badge>
                {l.label && <span className="text-xs text-muted-foreground ml-auto">{l.label}</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function EsgReport({ data }) {
  return (
    <div>
      <h2 className="text-lg font-bold mb-1" style={{ fontFamily: 'Space Grotesk' }}>ESG & Supplier Diversity</h2>
      <p className="text-sm text-muted-foreground mb-6">Environmental, social, and governance metrics across your vendor portfolio.</p>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
        <div className="p-4 rounded-xl border bg-card text-center">
          <div className="text-3xl font-bold text-purple-600" style={{ fontFamily: 'Space Grotesk' }}>{data?.diverse_pct || 0}%</div>
          <div className="text-[10px] text-muted-foreground mt-1">Diverse Supplier Solutions</div>
        </div>
        <div className="p-4 rounded-xl border bg-card text-center">
          <div className="text-3xl font-bold text-emerald-600" style={{ fontFamily: 'Space Grotesk' }}>{data?.environmental_pct || 0}%</div>
          <div className="text-[10px] text-muted-foreground mt-1">Environmentally Certified</div>
        </div>
        <div className="p-4 rounded-xl border bg-card text-center">
          <div className="text-3xl font-bold text-blue-600" style={{ fontFamily: 'Space Grotesk' }}>{data?.total_assignments || 0}</div>
          <div className="text-[10px] text-muted-foreground mt-1">Total Deployments Tracked</div>
        </div>
      </div>
      {(data?.top_vendors || []).length > 0 && (
        <div className="mb-8">
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Top Certified Vendors</h3>
          <div className="space-y-1.5">
            {data.top_vendors.map((v, i) => (
              <div key={i} className="flex items-center justify-between p-2.5 rounded-lg border bg-card text-sm">
                <span className="font-medium">{v.vendor_name}</span>
                <Badge variant="secondary" className="font-mono text-xs">{v.cert_count} certs</Badge>
              </div>
            ))}
          </div>
        </div>
      )}
      {(data?.property_scores || []).length > 0 && (
        <div>
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Diversity by Property</h3>
          <div className="overflow-x-auto border rounded-lg">
            <table className="w-full text-xs">
              <thead className="bg-muted/40"><tr><th className="text-left p-2.5 font-semibold">Property</th><th className="p-2.5 text-center">Solutions</th><th className="p-2.5 text-center">Diverse</th><th className="p-2.5 text-center">%</th></tr></thead>
              <tbody>
                {data.property_scores.map((p, i) => (
                  <tr key={i} className="border-t hover:bg-muted/20">
                    <td className="p-2.5 font-medium">{p.property_name}</td>
                    <td className="p-2.5 text-center">{p.total}</td>
                    <td className="p-2.5 text-center text-purple-600 font-medium">{p.diverse}</td>
                    <td className="p-2.5 text-center"><span className={`font-bold ${p.pct >= 30 ? 'text-emerald-600' : p.pct > 0 ? 'text-amber-600' : 'text-muted-foreground'}`}>{p.pct}%</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {!data?.top_vendors?.length && <p className="text-sm text-muted-foreground text-center py-8">No ESG data yet. Add certifications to vendors in the Tech Database.</p>}
    </div>
  );
}


function PlatformGapReport({ data }) {
  return (
    <div>
      <h2 className="text-lg font-bold mb-1" style={{ fontFamily: 'Space Grotesk' }}>Platform Gap Matrix</h2>
      <p className="text-sm text-muted-foreground mb-6">Important platforms missing at each property ({data?.total_platforms || 0} tracked).</p>
      {(data?.properties || []).length === 0 ? <p className="text-sm text-muted-foreground text-center py-8">All properties have full coverage.</p> : (
        <div className="space-y-3">
          {(data?.properties || []).map((p, i) => (
            <div key={i} className="border rounded-lg p-3 bg-card">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold">{p.property_name}</span>
                <Badge variant="outline" className={`text-[10px] ${p.gap_count > 5 ? 'text-red-600 border-red-200' : 'text-amber-600 border-amber-200'}`}>{p.gap_count} gaps</Badge>
              </div>
              <div className="flex flex-wrap gap-1">{p.gaps.map((g, j) => <Badge key={j} variant="secondary" className="text-[9px]">{g.name}</Badge>)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MultiVendorReport({ data }) {
  return (
    <div>
      <h2 className="text-lg font-bold mb-1" style={{ fontFamily: 'Space Grotesk' }}>Multi-Vendor Conflicts</h2>
      <p className="text-sm text-muted-foreground mb-6">Properties using multiple vendors for the same platform type.</p>
      {(data?.conflicts || []).length === 0 ? <p className="text-sm text-muted-foreground text-center py-8">No multi-vendor conflicts detected.</p> : (
        <div className="overflow-x-auto border rounded-lg">
          <table className="w-full text-xs"><thead className="bg-muted/40"><tr><th className="text-left p-2.5 font-semibold">Property</th><th className="text-left p-2.5">Platform</th><th className="p-2.5 text-center">Vendors</th><th className="text-left p-2.5">Details</th></tr></thead>
          <tbody>{(data?.conflicts || []).map((c, i) => (
            <tr key={i} className="border-t hover:bg-muted/20"><td className="p-2.5 font-medium">{c.property_name}</td><td className="p-2.5">{c.platform_name}</td><td className="p-2.5 text-center font-bold text-amber-600">{c.vendor_count}</td><td className="p-2.5"><div className="flex flex-wrap gap-1">{c.vendors.map((v,j) => <Badge key={j} variant="secondary" className="text-[9px]">{v}</Badge>)}</div></td></tr>
          ))}</tbody></table>
        </div>
      )}
    </div>
  );
}

function SsoDetailReport({ data, token, onRefresh }) {
  const [bulkGovOpen, setBulkGovOpen] = useState(false);
  const noSsoItems = data?.no_sso_items || [];
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>SSO Coverage Detail</h2>
        <Button variant="outline" size="sm" className="gap-1 text-xs" onClick={() => setBulkGovOpen(true)} disabled={noSsoItems.length === 0} data-testid="sso-bulk-set-btn">
          <Shield className="h-3 w-3" /> Bulk Set Auth Method ({noSsoItems.length})
        </Button>
      </div>
      <p className="text-sm text-muted-foreground mb-6">Per-property SSO adoption with non-SSO system breakdown.</p>
      {(data?.properties || []).length === 0 ? <p className="text-sm text-muted-foreground text-center py-8">No SSO data tracked.</p> : (
        <div className="overflow-x-auto border rounded-lg">
          <table className="w-full text-xs"><thead className="bg-muted/40"><tr><th className="text-left p-2.5 font-semibold">Property</th><th className="p-2.5 text-center">Total</th><th className="p-2.5 text-center text-emerald-700">SSO</th><th className="p-2.5 text-center text-amber-700">Standalone</th><th className="p-2.5 text-center">No Info</th><th className="p-2.5 text-center">SSO %</th><th className="text-left p-2.5">Non-SSO Systems</th></tr></thead>
          <tbody>{(data?.properties || []).map((p, i) => (
            <tr key={i} className="border-t hover:bg-muted/20"><td className="p-2.5 font-medium">{p.property_name}</td><td className="p-2.5 text-center">{p.total}</td><td className="p-2.5 text-center text-emerald-600">{p.sso_count}</td><td className="p-2.5 text-center text-amber-600">{p.standalone_count}</td><td className="p-2.5 text-center text-muted-foreground">{p.no_info_count}</td><td className="p-2.5 text-center"><span className={`font-bold ${p.sso_pct >= 50 ? 'text-emerald-600' : 'text-red-500'}`}>{p.sso_pct}%</span></td><td className="p-2.5"><div className="flex flex-wrap gap-1">{(p.non_sso_systems||[]).map((s,j) => <Badge key={j} variant="outline" className="text-[8px]">{s}</Badge>)}</div></td></tr>
          ))}</tbody></table>
        </div>
      )}

      <BulkGovernanceDialog open={bulkGovOpen} onOpenChange={setBulkGovOpen} token={token} items={noSsoItems} onComplete={onRefresh} />
    </div>
  );
}

function BrandGapDetailReport({ data }) {
  return (
    <div>
      <h2 className="text-lg font-bold mb-1" style={{ fontFamily: 'Space Grotesk' }}>Brand Gap Detail</h2>
      <p className="text-sm text-muted-foreground mb-6">Properties missing brand-required solutions by platform.</p>
      {(data?.properties || []).length === 0 ? <p className="text-sm text-muted-foreground text-center py-8">No brand compliance gaps detected.</p> : (
        <div className="space-y-3">
          {(data?.properties || []).map((p, i) => (
            <div key={i} className="border rounded-lg p-3 bg-card border-l-4 border-l-red-300">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold">{p.property_name}</span>
                <Badge className="text-[10px] bg-red-100 text-red-700">{p.missing_count} missing</Badge>
              </div>
              <div className="space-y-1">{(p.missing || []).map((m, j) => (
                <div key={j} className="flex items-center gap-2 text-xs"><Badge variant="outline" className="text-[9px]">{m.platform}</Badge><span className="text-muted-foreground">{m.brand} requires</span><span className="font-medium">{m.required_product}</span></div>
              ))}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}



function SupportCompletenessReport({ data, token, onRefresh }) {
  const [bulkContactOpen, setBulkContactOpen] = useState(false);
  const [bulkGovOpen, setBulkGovOpen] = useState(false);
  if (!data) return null;
  const s = data.summary || {};
  const missingItems = data.missing_items || [];
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>Support Info Completeness</h2>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="gap-1 text-xs" onClick={() => setBulkContactOpen(true)} disabled={missingItems.length === 0} data-testid="bulk-add-contacts-btn">
            <Headphones className="h-3 w-3" /> Bulk Add Contacts
          </Button>
          <Button variant="outline" size="sm" className="gap-1 text-xs" onClick={() => setBulkGovOpen(true)} disabled={missingItems.length === 0} data-testid="bulk-add-governance-btn">
            <Shield className="h-3 w-3" /> Bulk Set Governance
          </Button>
        </div>
      </div>
      <p className="text-sm text-muted-foreground mb-6">Shows which properties and platforms are missing support contact data needed for QR support cards.</p>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="p-3 rounded-lg border bg-card text-center">
          <div className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk', color: s.overall_completeness_pct >= 70 ? '#16a34a' : s.overall_completeness_pct >= 40 ? '#d97706' : '#dc2626' }}>{s.overall_completeness_pct || 0}%</div>
          <div className="text-[10px] text-muted-foreground mt-0.5">Overall Completeness</div>
        </div>
        <div className="p-3 rounded-lg border bg-card text-center">
          <div className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>{s.with_support_contact || 0}<span className="text-sm text-muted-foreground">/{s.total_assignments || 0}</span></div>
          <div className="text-[10px] text-muted-foreground mt-0.5">Have Support Contact</div>
        </div>
        <div className="p-3 rounded-lg border bg-card text-center">
          <div className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>{s.with_support_url || 0}<span className="text-sm text-muted-foreground">/{s.total_assignments || 0}</span></div>
          <div className="text-[10px] text-muted-foreground mt-0.5">Have Support URL</div>
        </div>
        <div className="p-3 rounded-lg border bg-card text-center">
          <div className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>{s.with_governance || 0}<span className="text-sm text-muted-foreground">/{s.total_assignments || 0}</span></div>
          <div className="text-[10px] text-muted-foreground mt-0.5">Have Governance Owner</div>
        </div>
      </div>

      {/* Property table */}
      <div id="report-content" className="border rounded-lg overflow-hidden mb-6">
        <table className="w-full text-sm">
          <thead><tr className="bg-muted/50">
            <th className="p-2.5 text-left text-xs font-semibold">Property</th>
            <th className="p-2.5 text-center text-xs font-semibold">Platforms</th>
            <th className="p-2.5 text-center text-xs font-semibold">Contact</th>
            <th className="p-2.5 text-center text-xs font-semibold">URL</th>
            <th className="p-2.5 text-center text-xs font-semibold">Governance</th>
            <th className="p-2.5 text-center text-xs font-semibold">Score</th>
          </tr></thead>
          <tbody>
            {(data.properties || []).map((p, i) => (
              <tr key={i} className="border-t hover:bg-muted/20">
                <td className="p-2.5 font-medium">{p.property_name}</td>
                <td className="p-2.5 text-center font-mono text-xs">{p.total_platforms}</td>
                <td className="p-2.5 text-center"><span className={`font-mono text-xs ${p.has_contact === p.total_platforms ? 'text-green-600' : p.has_contact > 0 ? 'text-amber-600' : 'text-red-500'}`}>{p.has_contact}/{p.total_platforms}</span></td>
                <td className="p-2.5 text-center"><span className={`font-mono text-xs ${p.has_url === p.total_platforms ? 'text-green-600' : p.has_url > 0 ? 'text-amber-600' : 'text-red-500'}`}>{p.has_url}/{p.total_platforms}</span></td>
                <td className="p-2.5 text-center"><span className={`font-mono text-xs ${p.has_governance === p.total_platforms ? 'text-green-600' : p.has_governance > 0 ? 'text-amber-600' : 'text-red-500'}`}>{p.has_governance}/{p.total_platforms}</span></td>
                <td className="p-2.5 text-center">
                  <div className="inline-flex items-center gap-1.5">
                    <div className="w-12 h-1.5 rounded-full bg-muted overflow-hidden"><div className="h-full rounded-full" style={{ width: `${p.completeness_pct}%`, backgroundColor: p.completeness_pct >= 70 ? '#16a34a' : p.completeness_pct >= 40 ? '#d97706' : '#dc2626' }} /></div>
                    <span className="text-[10px] font-mono">{p.completeness_pct}%</span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Missing items */}
      {(data.missing_items || []).length > 0 && (
        <div>
          <h3 className="text-sm font-semibold mb-2">Top Missing Support Info</h3>
          <div className="space-y-1">
            {(data.missing_items || []).slice(0, 20).map((m, i) => (
              <div key={i} className="flex items-center gap-2 text-xs p-2 rounded border bg-card">
                <span className="font-medium w-40 truncate">{m.property_name}</span>
                <Badge variant="outline" className="text-[9px] shrink-0">{m.platform}</Badge>
                <span className="text-muted-foreground truncate">{m.product}</span>
                <div className="flex gap-1 ml-auto shrink-0">
                  {m.missing_contact && <Badge className="text-[8px] bg-red-100 text-red-700">No Contact</Badge>}
                  {m.missing_url && <Badge className="text-[8px] bg-amber-100 text-amber-700">No URL</Badge>}
                  {m.missing_governance && <Badge className="text-[8px] bg-slate-100 text-slate-600">No Owner</Badge>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bulk editor dialogs */}
      <BulkSupportContactDialog open={bulkContactOpen} onOpenChange={setBulkContactOpen} token={token} items={missingItems.filter(m => m.missing_contact)} onComplete={onRefresh} />
      <BulkGovernanceDialog open={bulkGovOpen} onOpenChange={setBulkGovOpen} token={token} items={missingItems.filter(m => m.missing_governance)} onComplete={onRefresh} />
    </div>
  );
}


// ── Vendor Deployment Map ──
function VendorDeploymentMap({ data }) {
  const vendors = data?.vendors || [];
  const summary = data?.summary || {};
  const [expanded, setExpanded] = useState(null);
  return (
    <div>
      <h2 className="text-lg font-bold mb-1" style={{ fontFamily: 'Space Grotesk' }}>Vendor Deployment Map</h2>
      <p className="text-sm text-muted-foreground mb-4">Which vendors are deployed where across your portfolio.</p>
      <div className="grid grid-cols-3 gap-3 mb-6">
        <div className="p-3 rounded-lg border bg-card text-center"><div className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>{summary.total_vendors}</div><div className="text-[10px] text-muted-foreground">Active Vendors</div></div>
        <div className="p-3 rounded-lg border bg-card text-center"><div className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>{summary.total_properties}</div><div className="text-[10px] text-muted-foreground">Properties</div></div>
        <div className="p-3 rounded-lg border bg-card text-center"><div className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>{summary.total_deployments}</div><div className="text-[10px] text-muted-foreground">Total Deployments</div></div>
      </div>
      <div className="space-y-1.5">
        {vendors.map(v => (
          <div key={v.vendor_id} className="border rounded-lg bg-card overflow-hidden">
            <button onClick={() => setExpanded(expanded === v.vendor_id ? null : v.vendor_id)} className="w-full flex items-center gap-3 p-3 text-left hover:bg-muted/30" data-testid={`vendor-row-${v.vendor_id}`}>
              <div className="flex-1 min-w-0"><span className="text-sm font-medium">{v.vendor_name}</span></div>
              <Badge variant="outline" className="text-[9px] shrink-0">{v.product_count} products</Badge>
              <div className="text-right shrink-0">
                <div className="text-xs font-mono">{v.property_count}/{summary.total_properties} props</div>
                <div className="w-16 h-1.5 rounded-full bg-muted mt-1"><div className="h-full rounded-full bg-emerald-500" style={{ width: `${v.coverage_pct}%` }} /></div>
              </div>
              <span className="text-xs font-mono text-muted-foreground">{v.deployment_count} deploys</span>
            </button>
            {expanded === v.vendor_id && (
              <div className="px-3 pb-3 border-t bg-muted/10">
                <div className="flex gap-4 pt-2 text-xs">
                  <div><span className="text-muted-foreground">Platforms: </span>{v.platforms?.join(', ')}</div>
                </div>
                <div className="mt-2 flex flex-wrap gap-1">{v.properties?.map(p => <Badge key={p.id} variant="outline" className="text-[9px]">{p.name}</Badge>)}</div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Vendor Product Adoption ──
function VendorProductAdoption({ data }) {
  const vendors = data?.vendors || [];
  const [expanded, setExpanded] = useState(null);
  return (
    <div>
      <h2 className="text-lg font-bold mb-1" style={{ fontFamily: 'Space Grotesk' }}>Vendor Product Adoption</h2>
      <p className="text-sm text-muted-foreground mb-6">Which vendor products are deployed and where gaps exist.</p>
      <div className="space-y-1.5">
        {vendors.map(v => (
          <div key={v.vendor_id} className="border rounded-lg bg-card overflow-hidden">
            <button onClick={() => setExpanded(expanded === v.vendor_id ? null : v.vendor_id)} className="w-full flex items-center gap-3 p-3 text-left hover:bg-muted/30">
              <div className="flex-1 min-w-0"><span className="text-sm font-medium">{v.vendor_name}</span></div>
              <span className="text-xs text-muted-foreground">{v.deployed_products}/{v.total_products} adopted</span>
              <div className="w-20 h-1.5 rounded-full bg-muted"><div className="h-full rounded-full bg-blue-500" style={{ width: `${v.adoption_pct}%` }} /></div>
              <span className="text-xs font-mono">{v.adoption_pct}%</span>
            </button>
            {expanded === v.vendor_id && (
              <div className="px-3 pb-3 border-t bg-muted/10">
                <table className="w-full text-xs mt-2">
                  <thead><tr className="text-muted-foreground"><th className="text-left p-1.5">Product</th><th className="text-left p-1.5">Platform</th><th className="text-center p-1.5">Deployed At</th><th className="text-center p-1.5">Status</th></tr></thead>
                  <tbody>{v.products?.map(p => (
                    <tr key={p.product_id} className={`border-t ${!p.is_deployed ? 'text-muted-foreground bg-muted/20' : ''}`}>
                      <td className="p-1.5 font-medium">{p.product_name}</td>
                      <td className="p-1.5">{p.platform}</td>
                      <td className="p-1.5 text-center font-mono">{p.deployed_at}</td>
                      <td className="p-1.5 text-center"><Badge variant="outline" className={`text-[8px] ${p.is_deployed ? 'text-emerald-600' : 'text-slate-400'}`}>{p.is_deployed ? 'Deployed' : 'Available'}</Badge></td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Vendor Overlap Analysis ──
function VendorOverlapReport({ data }) {
  const overlaps = data?.overlaps || [];
  return (
    <div>
      <h2 className="text-lg font-bold mb-1" style={{ fontFamily: 'Space Grotesk' }}>Vendor Overlap Analysis</h2>
      <p className="text-sm text-muted-foreground mb-6">Platform categories where multiple vendors compete across your portfolio.</p>
      {overlaps.length === 0 ? <p className="text-sm text-muted-foreground text-center py-12">No vendor overlap detected — each platform category uses a single vendor.</p> : (
        <div className="space-y-3">
          {overlaps.map(o => (
            <div key={o.platform_id} className="border rounded-lg p-4 bg-card">
              <div className="flex items-center justify-between mb-3">
                <div><h3 className="text-sm font-semibold">{o.platform_name}</h3><span className="text-[10px] text-muted-foreground">{o.property_count} properties</span></div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-[9px]">{o.vendor_count} vendors</Badge>
                  {o.consolidation_opportunity && <Badge className="text-[9px] bg-amber-100 text-amber-700">Consolidation opportunity</Badge>}
                </div>
              </div>
              <div className="space-y-1.5">
                {o.vendors?.map(v => (
                  <div key={v.vendor_id} className="flex items-center gap-2">
                    <span className="text-xs font-medium w-40 truncate">{v.vendor_name}</span>
                    <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden"><div className="h-full rounded-full bg-blue-500" style={{ width: `${(v.property_count / o.property_count) * 100}%` }} /></div>
                    <span className="text-[10px] font-mono text-muted-foreground shrink-0">{v.property_count} props ({v.deployment_count} deploys)</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Brand Property Matrix ──
function BrandPropertyMatrix({ data }) {
  const properties = Array.isArray(data?.properties) ? data.properties : [];
  const brands = Array.isArray(data?.brands) ? data.brands : [];
  if (brands.length === 0) return <p className="text-sm text-muted-foreground text-center py-12">No brands configured. Add brands in the Tech Database.</p>;
  const statusColors = { compliant: 'bg-emerald-100 text-emerald-700', gap: 'bg-amber-100 text-amber-700', violation: 'bg-red-100 text-red-700' };
  return (
    <div>
      <h2 className="text-lg font-bold mb-1" style={{ fontFamily: 'Space Grotesk' }}>Brand Property Matrix</h2>
      <p className="text-sm text-muted-foreground mb-6">Compliance status per property per brand.</p>
      <div className="overflow-x-auto border rounded-lg">
        <table className="w-full text-xs">
          <thead className="bg-muted/40">
            <tr>
              <th className="text-left p-2.5 font-semibold sticky left-0 bg-muted/40">Property</th>
              {brands.map(b => <th key={b.id} className="p-2.5 text-center font-semibold">{b.name}</th>)}
            </tr>
          </thead>
          <tbody>
            {properties.map(p => (
              <tr key={p.property_id} className="border-t hover:bg-muted/20">
                <td className="p-2.5 font-medium sticky left-0 bg-card">{p.property_name}</td>
                {p.brands?.map(bc => (
                  <td key={bc.brand_id} className="p-2.5 text-center">
                    {bc.required === 0 ? <span className="text-muted-foreground">—</span> : (
                      <Badge className={`text-[8px] ${statusColors[bc.status] || ''}`}>{bc.met}/{bc.required}{bc.violations > 0 ? ` (${bc.violations} viol.)` : ''}</Badge>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Property Solution Inventory (self-fetching with property selector) ──
function PropertySolutionInventory({ token, properties }) {
  const [selectedProp, setSelectedProp] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetch = async (pid) => {
    if (!pid) return;
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/reports/property-solution-inventory?property_id=${pid}`, { headers });
      setData(res.data);
    } catch { setData(null); }
    finally { setLoading(false); }
  };

  return (
    <div>
      <h2 className="text-lg font-bold mb-1" style={{ fontFamily: 'Space Grotesk' }}>Property Solution Inventory</h2>
      <p className="text-sm text-muted-foreground mb-4">Complete list of deployed solutions for a specific property.</p>
      <div className="flex items-center gap-3 mb-6">
        <Select value={selectedProp} onValueChange={v => { setSelectedProp(v); fetch(v); }}>
          <SelectTrigger className="w-[300px]" data-testid="prop-inventory-select"><SelectValue placeholder="Select a property..." /></SelectTrigger>
          <SelectContent>{(properties || []).map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {loading && <div className="text-center py-12"><div className="animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full mx-auto" /></div>}
      {!loading && !selectedProp && <p className="text-sm text-muted-foreground text-center py-12">Select a property to view its full solution inventory.</p>}
      {!loading && data && (
        <>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-6">
            <div className="p-3 rounded-lg border bg-card text-center"><div className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>{data.summary.total_solutions}</div><div className="text-[9px] text-muted-foreground">Solutions</div></div>
            <div className="p-3 rounded-lg border bg-card text-center"><div className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>{data.summary.vendors}</div><div className="text-[9px] text-muted-foreground">Vendors</div></div>
            <div className="p-3 rounded-lg border bg-card text-center"><div className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>{data.summary.categories}</div><div className="text-[9px] text-muted-foreground">Categories</div></div>
            <div className="p-3 rounded-lg border bg-card text-center"><div className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>${data.summary.total_annual_cost?.toLocaleString()}</div><div className="text-[9px] text-muted-foreground">Annual Cost</div></div>
            <div className="p-3 rounded-lg border bg-card text-center"><div className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>{data.summary.with_contracts}</div><div className="text-[9px] text-muted-foreground">With Contracts</div></div>
            <div className="p-3 rounded-lg border bg-card text-center"><div className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>{data.summary.with_governance}</div><div className="text-[9px] text-muted-foreground">With Governance</div></div>
          </div>
          <div className="border rounded-lg overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-muted/40"><tr>
                <th className="p-2.5 text-left font-semibold">Category</th>
                <th className="p-2.5 text-left font-semibold">Platform</th>
                <th className="p-2.5 text-left font-semibold">Product</th>
                <th className="p-2.5 text-left font-semibold">Vendor</th>
                <th className="p-2.5 text-center font-semibold">Contract</th>
                <th className="p-2.5 text-right font-semibold">Annual Cost</th>
                <th className="p-2.5 text-left font-semibold">Renewal</th>
                <th className="p-2.5 text-left font-semibold">Admin</th>
                <th className="p-2.5 text-left font-semibold">Auth</th>
              </tr></thead>
              <tbody>
                {data.solutions?.map((s, i) => (
                  <tr key={i} className="border-t hover:bg-muted/20">
                    <td className="p-2.5"><Badge variant="outline" className="text-[8px]">{s.category}</Badge></td>
                    <td className="p-2.5 font-medium">{s.platform_name}</td>
                    <td className="p-2.5">{s.product_name}</td>
                    <td className="p-2.5">{s.vendor_name}</td>
                    <td className="p-2.5 text-center">{s.has_contract ? <Badge className="text-[8px] bg-emerald-100 text-emerald-700">Yes</Badge> : <Badge variant="outline" className="text-[8px] text-muted-foreground">No</Badge>}</td>
                    <td className="p-2.5 text-right font-mono">{s.annual_cost ? `$${s.annual_cost.toLocaleString()}` : '—'}</td>
                    <td className="p-2.5">{s.renewal_date || '—'}</td>
                    <td className="p-2.5">{s.admin || <span className="text-muted-foreground">—</span>}</td>
                    <td className="p-2.5">{s.auth_method || <span className="text-muted-foreground">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
